import { useState, useRef, useEffect } from 'react';
import RecurrenteConfigModal from '../../components/RecurrenteConfigModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Delete,
  Search,
  Save,
  Send,
  FileText,
  FileUp,
  User,
  UserPlus,
  Package,
  CreditCard,
  DollarSign,
  MessageSquare,
  Eye,
  X,
  ChevronDown,
  Briefcase,
  GripVertical,
  RefreshCw,
  Settings,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { documentsApi, receptoresApi, productosApi, settingsApi, parametrosApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import InvoiceTemplate from '../../components/InvoiceTemplate';
import DraggableModal from '../../components/DraggableModal';
import ConfirmModal from '../../components/ConfirmModal';
import CreditConfirmModal from '../../components/CreditConfirmModal';
import FormularioCliente from '../clientes/FormularioCliente';
import FormularioProducto from '../productos/FormularioProducto';
import ExoneracionModal from '../../components/ExoneracionModal';

const TIPOS_DOCUMENTO = [
  { value: '01', label: 'Factura Electrónica' },
  { value: '04', label: 'Tiquete Electrónico' },
];

const CONDICIONES_VENTA = [
  { value: '01', label: 'Contado' },
  { value: '02', label: 'Crédito' },
  { value: '03', label: 'Consignación' },
  { value: '05', label: 'Arrendamiento con opción de compra' },
  { value: '06', label: 'Arrendamiento en función financiera' },
  { value: '99', label: 'Otros' },
];

const MEDIOS_PAGO = [
  { value: '01', label: 'Efectivo', icon: DollarSign },
  { value: '02', label: 'Tarjeta', icon: CreditCard },
  { value: '03', label: 'Cheque', icon: FileText },
  { value: '04', label: 'Transferencia', icon: Send },
  { value: '99', label: 'Otros', icon: FileText },
];

// Tipo de cambio del DÍA DE EMISIÓN (BCCR, tabla tipos_cambio) para un documento en dólares que se
// carga para emitirse: una copia o un borrador (recurrente, rechazado) traen el de su fecha original y
// salían a Hacienda con un tipo de cambio viejo (25-oct-2026: FE de octubre con el de septiembre).
async function tipoCambioDeHoy(anterior) {
  const st = useAuthStore.getState();
  const hoy = await st.fetchTipoCambio().catch(() => null);
  return hoy || st.tipoCambio || anterior || 1;
}

// ── Periodo de cobro ───────────────────────────────────────────────────
// Se guarda como texto ya armado en documentos.periodo_cobro_texto, igual que
// lo hace el motor de recurrentes. Formatos que existen en produccion:
// "Periodo de Cobro: Septiembre 2026" y "Periodo de Cobro: Año 2026".
const MESES_PERIODO = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function armarPeriodoCobro({ activo, etiqueta, tipo, mes, anio, libre }) {
  if (!activo) return null;
  // Texto que no calza con los formatos conocidos: se respeta tal cual.
  if (libre) return String(libre).trim() || null;
  const label = (etiqueta || 'Periodo de Cobro').trim();
  const periodo = tipo === 'anio' ? `Año ${anio}` : `${MESES_PERIODO[mes]} ${anio}`;
  return `${label}: ${periodo}`;
}

// Deshace armarPeriodoCobro() para volver a poblar el formulario al cargar o
// copiar un documento. Si el texto no calza con ninguno de los dos formatos
// (por ejemplo uno viejo escrito a mano), se respeta tal cual en `libre` para
// no perderlo al guardar.
function leerPeriodoCobro(texto) {
  const hoy = new Date();
  const base = { activo: false, etiqueta: 'Periodo de Cobro', tipo: 'mes', mes: hoy.getMonth(), anio: hoy.getFullYear() };
  if (!texto || !String(texto).trim()) return base;

  const crudo = String(texto).trim();
  const sep = crudo.indexOf(':');
  const etiqueta = sep > 0 ? crudo.slice(0, sep).trim() : 'Periodo de Cobro';
  const periodo = sep > 0 ? crudo.slice(sep + 1).trim() : crudo;

  const anual = periodo.match(/^Año\s+(\d{4})$/i);
  if (anual) return { ...base, activo: true, etiqueta, tipo: 'anio', anio: parseInt(anual[1]) };

  const mensual = periodo.match(/^(\S+)\s+(\d{4})$/);
  if (mensual) {
    const idx = MESES_PERIODO.findIndex(m => m.toLowerCase() === mensual[1].toLowerCase());
    if (idx >= 0) return { ...base, activo: true, etiqueta, tipo: 'mes', mes: idx, anio: parseInt(mensual[2]) };
  }

  return { ...base, activo: true, etiqueta, libre: crudo };
}

// Los tipos de exoneración se cargan desde la API

// Toolbar button component - Fully responsive
function ToolbarButton({ icon: Icon, label, onClick, disabled, active, variant = 'default' }) {
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500",
    warning: "border border-amber-400 text-amber-600 hover:bg-amber-50 hover:border-amber-500",
    success: "border border-green-400 text-green-600 hover:bg-green-50 hover:border-green-500",
    danger: "border border-red-400 text-red-500 hover:bg-red-50 hover:border-red-500",
    purple: "border border-purple-400 text-purple-600 hover:bg-purple-50 hover:border-purple-500",
    indigo: "border border-indigo-400 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-500",
    cyan: "border border-cyan-400 text-cyan-600 hover:bg-cyan-50 hover:border-cyan-500",
    teal: "border border-teal-400 text-teal-600 hover:bg-teal-50 hover:border-teal-500",
    slate: "border border-slate-400 text-slate-600 hover:bg-slate-50 hover:border-slate-500",
    rose: "border border-rose-400 text-rose-600 hover:bg-rose-50 hover:border-rose-500",
    emerald: "border border-emerald-400 text-emerald-600 hover:bg-emerald-50 hover:border-emerald-500",
    violet: "border border-violet-400 text-violet-600 hover:bg-violet-50 hover:border-violet-500",
    orange: "border border-orange-400 text-orange-600 hover:bg-orange-50 hover:border-orange-500",
    lime: "border border-lime-500 text-lime-600 hover:bg-lime-50 hover:border-lime-600",
    sky: "border border-sky-400 text-sky-600 hover:bg-sky-50 hover:border-sky-500",
  };

  const activeStyles = {
    primary: 'bg-blue-50 border-blue-500 border-2',
    warning: 'bg-amber-50 border-amber-500 border-2',
    success: 'bg-green-50 border-green-500 border-2',
    danger: 'bg-red-50 border-red-500 border-2',
    purple: 'bg-purple-50 border-purple-500 border-2',
    indigo: 'bg-indigo-50 border-indigo-500 border-2',
    cyan: 'bg-cyan-50 border-cyan-500 border-2',
    teal: 'bg-teal-50 border-teal-500 border-2',
    slate: 'bg-slate-100 border-slate-500 border-2',
    rose: 'bg-rose-50 border-rose-500 border-2',
    emerald: 'bg-emerald-50 border-emerald-500 border-2',
    violet: 'bg-violet-50 border-violet-500 border-2',
    orange: 'bg-orange-50 border-orange-500 border-2',
    lime: 'bg-lime-50 border-lime-600 border-2',
    sky: 'bg-sky-50 border-sky-500 border-2',
    default: 'bg-gray-100 border-gray-400',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`
        flex flex-col items-center justify-center
        px-1 py-1 rounded-lg transition-all font-medium
        toolbar-btn
        ${variants[variant]}
        ${active ? activeStyles[variant] || activeStyles.default : ''}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <Icon className="toolbar-icon mb-0.5" />
      <span className="toolbar-label whitespace-nowrap">{label}</span>
    </button>
  );
}

// Separator for toolbar
function ToolbarSeparator() {
  return <div className="toolbar-separator w-px bg-gray-300" />;
}

export default function CrearFactura() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentEmisor, setPageStatus, clearPageStatus, tipoCambio: storeTipoCambio } = useAuthStore();

  const [loading, setLoading] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [searchingClient, setSearchingClient] = useState(false);
  const [showNuevoClienteModal, setShowNuevoClienteModal] = useState(false);
  const [showNuevoProductoModal, setShowNuevoProductoModal] = useState(false);
  const [nuevoProductoNombre, setNuevoProductoNombre] = useState('');
  const [clientHighlightedIndex, setClientHighlightedIndex] = useState(-1);

  const productInputRef = useRef(null);
  const productListRef = useRef(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const [receptor, setReceptor] = useState(null);
  const [actividadesCliente, setActividadesCliente] = useState([]);
  const [cargandoActividades, setCargandoActividades] = useState(false);
  const [documento, setDocumento] = useState({
    tipoDocumento: '01',
    condicionVenta: '02',
    medioPago: '01',
    moneda: 'USD',
    tipoCambio: storeTipoCambio || 1,
    comentarios: '',
    diasCredito: 30,
    ordenCompra: ''
  });
  // Periodo de cobro — la misma opcion que tienen las plantillas recurrentes,
  // pero para una FE suelta. Se guarda ya armado en documentos.periodo_cobro_texto
  // ("Periodo de Cobro: Septiembre 2026") y solo sale en el PDF, no en el XML.
  const [periodoCobro, setPeriodoCobro] = useState(() => {
    const hoy = new Date();
    return {
      activo: false,
      etiqueta: 'Periodo de Cobro',
      tipo: 'mes',            // 'mes' | 'anio'
      mes: hoy.getMonth(),    // 0-11
      anio: hoy.getFullYear()
    };
  });
  const [showOpcionesFactura, setShowOpcionesFactura] = useState(false);
  const [lineas, setLineas] = useState([]);
  const [selectedLineIds, setSelectedLineIds] = useState([]);
  const [draggedLineId, setDraggedLineId] = useState(null);

  // Document being edited
  const [documentoId, setDocumentoId] = useState(null);

  // Flag for editing rejected documents (to show warning and keep consecutivo)
  const [isRejectedEdit, setIsRejectedEdit] = useState(false);
  const [rejectedConsecutivo, setRejectedConsecutivo] = useState(null);

  // Ref to prevent duplicate loading
  const isLoadingDocumentRef = useRef(false);

  // Preview modal
  const [showPreview, setShowPreview] = useState(false);
  const [emisorData, setEmisorData] = useState(null);

  // Recurrente flag
  const [esRecurrente, setEsRecurrente] = useState(false);
  // Config de plantilla recurrente (se setea desde el modal). Si no es null,
  // al aceptar la FE en Hacienda se creará la plantilla con esta config.
  const [recurringConfig, setRecurringConfig] = useState(null);
  const [showRecurrenteModal, setShowRecurrenteModal] = useState(false);

  // Modal de validación de exoneración
  const [showExoneracionModal, setShowExoneracionModal] = useState(false);
  const [exoneracionValidation, setExoneracionValidation] = useState(null);
  const [pendingEnviar, setPendingEnviar] = useState(false);

  // Modal de confirmación de crédito/contado
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showFacturarModal, setShowFacturarModal] = useState(false);
  const [creditModalMode, setCreditModalMode] = useState('credito'); // 'credito' | 'contado'
  const [pendingCreditEnviar, setPendingCreditEnviar] = useState(false);
  const [skipCreditValidation, setSkipCreditValidation] = useState(false);

  // Modal para agregar exoneración rápida
  const [showExoneracionRapidaModal, setShowExoneracionRapidaModal] = useState(false);
  const [exoneracionTemporal, setExoneracionTemporal] = useState(null);

  // Tarifas de IVA
  const [tarifasIva, setTarifasIva] = useState([]);
  const tarifaDefault = tarifasIva.find(t => t.codigo === '08') || { codigo: '08', porcentaje: 13, descripcion: 'Tarifa general 13%' };

  // Confirmation modal
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'warning'
  });

  const showConfirm = (title, message, onConfirm, variant = 'warning') => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm,
      variant
    });
  };

  const closeConfirm = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
  };

  // Load emisor data for preview
  useEffect(() => {
    const loadEmisorData = async () => {
      try {
        const response = await settingsApi.getEmisor();
        if (response.data?.data) {
          setEmisorData(response.data.data);
        }
      } catch (error) {
        if (error?.code !== 'ERR_CANCELED') console.error('Error loading emisor:', error);
      }
    };
    loadEmisorData();
  }, [currentEmisor]);

  // Cargar tarifas de IVA desde la API
  useEffect(() => {
    const cargarTarifasIva = async () => {
      try {
        const response = await parametrosApi.listTarifasIva();
        setTarifasIva(response.data?.data || []);
      } catch (error) {
        if (error?.code !== 'ERR_CANCELED') console.error('Error cargando tarifas de IVA:', error);
      }
    };
    cargarTarifasIva();
  }, []);

  // Persistir exoneración temporal en localStorage (asociada al cliente)
  useEffect(() => {
    if (exoneracionTemporal && receptor?.id) {
      localStorage.setItem('exoneracionTemporal', JSON.stringify({
        receptorId: receptor.id,
        data: exoneracionTemporal
      }));
    } else if (!exoneracionTemporal) {
      localStorage.removeItem('exoneracionTemporal');
    }
  }, [exoneracionTemporal, receptor?.id]);

  // Recuperar exoneración temporal al cargar o cambiar cliente
  useEffect(() => {
    if (receptor?.id) {
      const saved = localStorage.getItem('exoneracionTemporal');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.receptorId === receptor.id && parsed.data) {
            setExoneracionTemporal(parsed.data);
          } else {
            // Es de otro cliente, limpiar
            setExoneracionTemporal(null);
          }
        } catch {
          setExoneracionTemporal(null);
        }
      }
    } else {
      setExoneracionTemporal(null);
    }
  }, [receptor?.id]);

  // Set page status in header
  useEffect(() => {
    const statusText = 'Facturando...';
    const subtext = `${documento.moneda === 'USD' ? `$ Dólares (TC: ₡${documento.tipoCambio?.toFixed(2) || '---'})` : '₡ Colones'} • ${documento.condicionVenta === '02' ? 'Crédito' : 'Contado'}`;
    setPageStatus({
      text: statusText,
      subtext: subtext,
      gradient: 'blue'
    });

    // Clear status when leaving the page
    return () => clearPageStatus();
  }, [documento.tipoDocumento, documento.moneda, documento.tipoCambio, documento.condicionVenta, setPageStatus, clearPageStatus]);

  // Load document if cargar param is present, or set tipo if specified, or copy
  useEffect(() => {
    const cargarId = searchParams.get('cargar');
    const copyId = searchParams.get('copy');
    const tipo = searchParams.get('tipo');

    if (cargarId) {
      // Prevent duplicate loading (React StrictMode calls effects twice)
      if (isLoadingDocumentRef.current) return;
      isLoadingDocumentRef.current = true;

      // Check if this is a rejected document edit
      const isRejected = searchParams.get('rejected') === '1';

      // Remove param from URL first to prevent re-triggering
      setSearchParams({}, { replace: true });
      cargarDocumento(cargarId, isRejected).finally(() => {
        isLoadingDocumentRef.current = false;
      });
    } else if (copyId) {
      // Copy document - load data but don't set documentoId (creates new)
      // Use async IIFE to wait for copy to complete before clearing params
      (async () => {
        await copiarDocumento(copyId);
        // Remove param from URL after copy completes
        setSearchParams({}, { replace: true });
      })();
    }
  }, [searchParams]);

  // Flag to track if initial load is done
  const [isInitialized, setIsInitialized] = useState(false);

  // Load from localStorage on mount (runs first)
  useEffect(() => {
    const cargarId = searchParams.get('cargar');
    if (!cargarId) {
      const saved = localStorage.getItem('factura_en_progreso');
      if (saved) {
        try {
          const data = JSON.parse(saved);
          if (data.lineas && data.lineas.length > 0) {
            setReceptor(data.receptor);
            setDocumento(data.documento);
            setLineas(data.lineas);
            setDocumentoId(data.documentoId);
            if (data.esRecurrente) setEsRecurrente(data.esRecurrente);
          }
        } catch (e) {
          console.error('Error loading saved invoice:', e);
        }
      }
    }
    // Mark as initialized after attempting to load
    setTimeout(() => setIsInitialized(true), 100);
  }, []);

  // Sync exchange rate from store when available
  useEffect(() => {
    if (storeTipoCambio && documento.moneda === 'USD' && documento.tipoCambio <= 1) {
      setDocumento(prev => ({
        ...prev,
        tipoCambio: storeTipoCambio
      }));
    }
  }, [storeTipoCambio, documento.moneda]);

  // Reset client search when modal opens
  useEffect(() => {
    if (showClientModal) {
      setClientHighlightedIndex(-1);
      setClientSearchTerm('');
      setClientSearchResults([]);
    }
  }, [showClientModal]);

  // Load activities when receptor changes (e.g., from localStorage)
  useEffect(() => {
    const cargarActividades = async () => {
      if (receptor?.identificacion && actividadesCliente.length === 0 && !cargandoActividades) {
        setCargandoActividades(true);
        try {
          const response = await receptoresApi.consultarHacienda(receptor.identificacion);
          if (response.data?.success && response.data?.data?.actividades) {
            setActividadesCliente(response.data.data.actividades);
          }
        } catch (error) {
          console.error('Error fetching activities:', error);
        } finally {
          setCargandoActividades(false);
        }
      }
    };

    if (isInitialized && receptor) {
      cargarActividades();
    }
  }, [receptor?.identificacion, isInitialized]);

  // Auto-save to localStorage (only after initialization)
  useEffect(() => {
    if (!isInitialized) return; // Don't save until initialized

    const dataToSave = {
      receptor,
      documento,
      lineas,
      documentoId,
      esRecurrente,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem('factura_en_progreso', JSON.stringify(dataToSave));
  }, [receptor, documento, lineas, documentoId, esRecurrente, isInitialized]);

  // Deriva el % de descuento a partir del monto guardado en la línea (la BD no guarda el %).
  // Si en algún momento la línea trae el % explícito, se respeta.
  const descuentoPctDesdeMonto = (l) => {
    if (l.descuentoPorcentaje != null && parseFloat(l.descuentoPorcentaje) > 0) {
      return parseFloat(l.descuentoPorcentaje);
    }
    const bruto = (parseFloat(l.cantidad) || 1) * (parseFloat(l.precioUnitario) || 0);
    const monto = parseFloat(l.descuentoMonto) || 0;
    if (bruto <= 0 || monto <= 0) return 0;
    return (monto / bruto) * 100;
  };

  // Cargar un documento por ID
  const cargarDocumento = async (id, isRejected = false) => {
    try {
      setLoading(true);
      const response = await documentsApi.getById(id);
      const data = response.data?.data;

      if (data) {
        // Cargar receptor si existe - obtener datos completos incluyendo exoneración
        if (data.receptorId) {
          try {
            const receptorResponse = await receptoresApi.getById(data.receptorId);
            if (receptorResponse.data?.data) {
              const r = receptorResponse.data.data;
              setReceptor({
                id: r.id,
                nombre: r.nombre,
                identificacion: r.identificacion,
                tipoIdentificacion: r.tipoIdentificacion || r.tipo_identificacion,
                email: r.email,
                emailFacturacion: r.emailFacturacion || r.email_facturacion,
                telefonoCodigo: r.telefonoCodigo || r.telefono_codigo,
                telefonoNumero: r.telefonoNumero || r.telefono_numero,
                provincia: r.provincia,
                canton: r.canton,
                distrito: r.distrito,
                otrasSenas: r.otrasSenas || r.otras_senas,
                diasCredito: r.diasCredito || r.dias_credito,
                actividadEconomica: r.actividadEconomica || r.actividad_economica,
                actividadEconomicaDescripcion: r.actividadEconomicaDescripcion || r.actividad_economica_descripcion,
                // Campos de exoneración
                exoneracionActiva: r.exoneracionActiva || r.exoneracion_activa,
                exoneracionNumero: r.exoneracionNumero || r.exoneracion_numero,
                exoneracionTipoDoc: r.exoneracionTipoDoc || r.exoneracion_tipo_doc,
                exoneracionTipoDocDesc: r.exoneracionTipoDocDesc || r.exoneracion_tipo_doc_desc,
                exoneracionInstitucionCodigo: r.exoneracionInstitucionCodigo || r.exoneracion_institucion_codigo,
                exoneracionInstitucionNombre: r.exoneracionInstitucionNombre || r.exoneracion_institucion_nombre,
                exoneracionFechaEmision: r.exoneracionFechaEmision || r.exoneracion_fecha_emision,
                exoneracionFechaVencimiento: r.exoneracionFechaVencimiento || r.exoneracion_fecha_vencimiento,
                exoneracionPorcentaje: r.exoneracionPorcentaje || r.exoneracion_porcentaje,
                exoneracionCabys: r.exoneracionCabys || r.exoneracion_cabys,
                exoneraciones: r.exoneraciones || null
              });
            } else {
              setReceptor({
                id: data.receptorId,
                nombre: data.receptorNombre || 'Cliente',
                identificacion: data.receptorIdentificacion
              });
            }
          } catch (e) {
            console.error('Error loading receptor:', e);
            setReceptor({
              id: data.receptorId,
              nombre: data.receptorNombre || 'Cliente',
              identificacion: data.receptorIdentificacion
            });
          }
        } else {
          setReceptor(null);
        }

        // Cargar documento
        setDocumento({
          tipoDocumento: data.tipoDocumento || '01',
          condicionVenta: data.condicionVenta || '01',
          medioPago: data.medioPago?.[0] || '01',
          moneda: data.codigoMoneda || 'CRC',
          tipoCambio: data.codigoMoneda === 'USD' ? await tipoCambioDeHoy(data.tipoCambio) : (data.tipoCambio || 1),
          comentarios: data.notas || '',
          diasCredito: data.plazoCredito || data.receptorDiasCredito || 30,
          ordenCompra: data.metadata?.ordenCompra || ''
        });
        setPeriodoCobro(leerPeriodoCobro(data.periodoCobroTexto || data.periodo_cobro_texto));
        // Si el borrador ya traía la configuración de recurrente, se restaura:
        // si no, al volver a guardar se perdería y la plantilla nunca se crearía.
        const pendiente = data.metadata?.pendingRecurring;
        if (pendiente && pendiente.frecuencia) {
          setRecurringConfig(pendiente);
          setEsRecurrente(true);
        }

        // Cargar líneas
        const lineasCargadas = (data.lineas || []).map((l, idx) => ({
          id: Date.now() + idx,
          cantidad: l.cantidad || 1,
          detalle: l.detalle,
          codigo: l.codigo,
          sku: l.sku || l.codigoComercial || '',
          precioUnitario: l.precioUnitario || 0,
          iva: l.impuestoTarifa || 13,
          codigoTarifa: l.impuestoCodigoTarifa || '08',
          // El JSON guardado en BD puede tener 2 formatos según cuándo se creó la FE:
          //   - Viejo (productos sin transformar): {codigoImpuesto, codigo, porcentaje, descripcion}
          //   - Nuevo (frontend submit):            {codigo, codigoTarifa, tarifa, monto}
          // Detectamos por la presencia de `codigoTarifa`/`tarifa`. Sin esto, leer mal los
          // campos producía NaN visible en "Cruz Roja (%) USDNaN" + "Total USDNaN".
          impuestosAdicionales: (l.impuestosAdicionales || []).map(imp => {
            const esFormatoNuevo = imp.codigoTarifa !== undefined || imp.tarifa !== undefined;
            if (esFormatoNuevo) {
              return {
                codigoImpuesto: imp.codigo,
                codigo: imp.codigoTarifa,
                descripcion: imp.descripcion || (imp.codigo === '07' ? 'Impuesto 911' : 'Cruz Roja'),
                porcentaje: parseFloat(imp.tarifa) || 0
              };
            }
            return {
              codigoImpuesto: imp.codigoImpuesto || imp.codigo_impuesto,
              codigo: imp.codigo,
              descripcion: imp.descripcion || (imp.codigoImpuesto === '07' ? 'Impuesto 911' : 'Cruz Roja'),
              porcentaje: parseFloat(imp.porcentaje) || 0
            };
          }),
          // El editor maneja `descuento` como PORCENTAJE, pero la línea guardada en BD solo
          // trae `descuentoMonto` (no el %). Derivamos el % del monto para no perder el descuento.
          descuento: descuentoPctDesdeMonto(l)
        }));
        setLineas(lineasCargadas);

        setDocumentoId(data.id);

        // Si es un documento rechazado, guardar info para el banner
        if (isRejected && data.status === 'rejected') {
          setIsRejectedEdit(true);
          setRejectedConsecutivo(data.consecutivo);
          toast.success('Documento rechazado cargado - Corrija los errores y reenvíe');
        } else {
          toast.success('Documento cargado');
        }
      }
    } catch (error) {
      console.error('Error loading document:', error);
      toast.error('Error al cargar documento');
    } finally {
      setLoading(false);
    }
  };

  // Copiar un documento (cargar datos pero NO el ID, para crear nuevo)
  const copiarDocumento = async (id) => {
    try {
      setLoading(true);
      const response = await documentsApi.getById(id);
      const data = response.data?.data;

      if (data) {
        // Cargar receptor si existe - obtener datos completos incluyendo exoneración
        if (data.receptorId) {
          try {
            const receptorResponse = await receptoresApi.getById(data.receptorId);
            if (receptorResponse.data?.data) {
              const r = receptorResponse.data.data;
              setReceptor({
                id: r.id,
                nombre: r.nombre,
                identificacion: r.identificacion,
                tipoIdentificacion: r.tipoIdentificacion || r.tipo_identificacion,
                email: r.email,
                emailFacturacion: r.emailFacturacion || r.email_facturacion,
                telefonoCodigo: r.telefonoCodigo || r.telefono_codigo,
                telefonoNumero: r.telefonoNumero || r.telefono_numero,
                provincia: r.provincia,
                canton: r.canton,
                distrito: r.distrito,
                otrasSenas: r.otrasSenas || r.otras_senas,
                diasCredito: r.diasCredito || r.dias_credito,
                actividadEconomica: r.actividadEconomica || r.actividad_economica,
                actividadEconomicaDescripcion: r.actividadEconomicaDescripcion || r.actividad_economica_descripcion,
                exoneracionActiva: r.exoneracionActiva || r.exoneracion_activa,
                exoneracionNumero: r.exoneracionNumero || r.exoneracion_numero,
                exoneracionTipoDoc: r.exoneracionTipoDoc || r.exoneracion_tipo_doc,
                exoneracionTipoDocDesc: r.exoneracionTipoDocDesc || r.exoneracion_tipo_doc_desc,
                exoneracionInstitucionCodigo: r.exoneracionInstitucionCodigo || r.exoneracion_institucion_codigo,
                exoneracionInstitucionNombre: r.exoneracionInstitucionNombre || r.exoneracion_institucion_nombre,
                exoneracionFechaEmision: r.exoneracionFechaEmision || r.exoneracion_fecha_emision,
                exoneracionFechaVencimiento: r.exoneracionFechaVencimiento || r.exoneracion_fecha_vencimiento,
                exoneracionPorcentaje: r.exoneracionPorcentaje || r.exoneracion_porcentaje,
                exoneracionCabys: r.exoneracionCabys || r.exoneracion_cabys,
                exoneraciones: r.exoneraciones || null
              });
            } else {
              setReceptor({
                id: data.receptorId,
                nombre: data.receptorNombre || 'Cliente',
                identificacion: data.receptorIdentificacion
              });
            }
          } catch (e) {
            console.error('Error loading receptor:', e);
            setReceptor({
              id: data.receptorId,
              nombre: data.receptorNombre || 'Cliente',
              identificacion: data.receptorIdentificacion
            });
          }
        } else {
          setReceptor(null);
        }

        // Cargar documento - siempre como nueva FE
        setDocumento({
          tipoDocumento: '01', // Siempre nueva FE
          condicionVenta: data.condicionVenta || '01',
          medioPago: data.medioPago?.[0] || '01',
          moneda: data.codigoMoneda || 'USD',
          tipoCambio: (data.codigoMoneda || 'USD') === 'USD' ? await tipoCambioDeHoy(data.tipoCambio) : 1,
          comentarios: data.notas || '', // Copiar notas/comentarios del original
          diasCredito: data.plazoCredito || data.receptorDiasCredito || 30,
          ordenCompra: data.metadata?.ordenCompra || data.ordenCompra || '' // Copiar OC también
        });
        // El periodo de cobro se replica tal cual; se edita antes de emitir.
        setPeriodoCobro(leerPeriodoCobro(data.periodoCobroTexto || data.periodo_cobro_texto));

        // Cargar líneas
        const lineasCargadas = (data.lineas || []).map((l, idx) => ({
          id: Date.now() + idx,
          cantidad: l.cantidad || 1,
          detalle: l.detalle,
          codigo: l.codigo,
          sku: l.sku || l.codigoComercial || '',
          precioUnitario: l.precioUnitario || 0,
          iva: l.impuestoTarifa || 13,
          codigoTarifa: l.impuestoCodigoTarifa || '08',
          // El JSON guardado en BD puede tener 2 formatos según cuándo se creó la FE:
          //   - Viejo (productos sin transformar): {codigoImpuesto, codigo, porcentaje, descripcion}
          //   - Nuevo (frontend submit):            {codigo, codigoTarifa, tarifa, monto}
          // Detectamos por la presencia de `codigoTarifa`/`tarifa`. Sin esto, leer mal los
          // campos producía NaN visible en "Cruz Roja (%) USDNaN" + "Total USDNaN".
          impuestosAdicionales: (l.impuestosAdicionales || []).map(imp => {
            const esFormatoNuevo = imp.codigoTarifa !== undefined || imp.tarifa !== undefined;
            if (esFormatoNuevo) {
              return {
                codigoImpuesto: imp.codigo,
                codigo: imp.codigoTarifa,
                descripcion: imp.descripcion || (imp.codigo === '07' ? 'Impuesto 911' : 'Cruz Roja'),
                porcentaje: parseFloat(imp.tarifa) || 0
              };
            }
            return {
              codigoImpuesto: imp.codigoImpuesto || imp.codigo_impuesto,
              codigo: imp.codigo,
              descripcion: imp.descripcion || (imp.codigoImpuesto === '07' ? 'Impuesto 911' : 'Cruz Roja'),
              porcentaje: parseFloat(imp.porcentaje) || 0
            };
          }),
          // El editor maneja `descuento` como PORCENTAJE, pero la línea guardada en BD solo
          // trae `descuentoMonto` (no el %). Derivamos el % del monto para no perder el descuento.
          descuento: descuentoPctDesdeMonto(l)
        }));
        setLineas(lineasCargadas);

        // NO setear documentoId - esto es una copia, se creará como nuevo
        setDocumentoId(null);
        setIsRejectedEdit(false);
        setRejectedConsecutivo(null);
        localStorage.removeItem('factura_en_progreso');
        toast.success('Documento copiado - Guarde para crear nueva factura');
      }
    } catch (error) {
      console.error('Error copying document:', error);
      toast.error('Error al copiar documento');
    } finally {
      setLoading(false);
    }
  };

  // Focus product search on load
  useEffect(() => {
    if (productInputRef.current && !searchParams.get('cargar')) {
      productInputRef.current.focus();
    }
  }, []);

  // Scroll highlighted product into view
  useEffect(() => {
    if (highlightedIndex >= 0 && productListRef.current) {
      const items = productListRef.current.children;
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Client search
  const buscarCliente = async (term) => {
    if (term.length < 2) {
      setClientSearchResults([]);
      return;
    }

    setSearchingClient(true);
    try {
      const response = await receptoresApi.search(term);
      setClientSearchResults(response.data?.data || []);
    } catch (error) {
      console.error('Error searching clients:', error);
      setClientSearchResults([]);
    } finally {
      setSearchingClient(false);
    }
  };

  // Product search with debounce
  useEffect(() => {
    if (productSearchTerm.length < 2) {
      setProductSearchResults([]);
      setShowProductDropdown(false);
      setHighlightedIndex(-1);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchingProducts(true);
      try {
        const response = await productosApi.list({ q: productSearchTerm, limit: 10 });
        setProductSearchResults(response.data?.data || []);
        setShowProductDropdown(true);
        setHighlightedIndex(-1); // Reset highlight on new search
      } catch (error) {
        console.error('Error searching products:', error);
        setProductSearchResults([]);
      } finally {
        setSearchingProducts(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [productSearchTerm]);

  // Toggle product selection for multi-select
  const toggleProductSelection = (producto) => {
    setSelectedProducts(prev => {
      const isSelected = prev.some(p => p.id === producto.id);
      if (isSelected) {
        return prev.filter(p => p.id !== producto.id);
      } else {
        return [...prev, producto];
      }
    });
  };

  // Add all selected products
  // Find the best matching exoneration for a product's CABYS.
  // IMPORTANT: returns the product's ORIGINAL tariff in `ivaFinal` — do NOT
  // pre-deduct the exoneration here. The deduction is applied at totals time
  // by calcularIVA/calcularMontoExonerado (and at XML build time at line ~2616).
  // Pre-deducting causes the exoneration to be applied twice and zeroes out
  // the IVA for tarifas reducidas (e.g. UTN 2% efectiva would show 0% IVA).
  const findExoneracionForProduct = (tarifaOriginal, productCabys, rec) => {
    if (!rec?.exoneracionActiva) return { ivaFinal: tarifaOriginal, exoneracionMatch: null };

    // Check multiple exoneraciones first
    const exoneraciones = rec.exoneraciones || [];
    if (exoneraciones.length > 0) {
      for (const exo of exoneraciones) {
        const exoCabys = exo.cabys;
        // If exoneration has no specific CABYS, it applies to everything
        if (!exoCabys || !Array.isArray(exoCabys) || exoCabys.length === 0) {
          return { ivaFinal: tarifaOriginal, exoneracionMatch: exo };
        }
        // Check if product CABYS matches any authorized CABYS
        if (productCabys && exoCabys.some(c => productCabys === c)) {
          return { ivaFinal: tarifaOriginal, exoneracionMatch: exo };
        }
      }
      // No match found in any exoneration
      return { ivaFinal: tarifaOriginal, exoneracionMatch: null };
    }

    // Fallback: legacy single exoneration
    if (rec.exoneracionPorcentaje) {
      const exoCabys = rec.exoneracionCabys;
      const cabysMatch = !exoCabys || !Array.isArray(exoCabys) || exoCabys.length === 0 ||
        exoCabys.some(c => productCabys === c);
      if (cabysMatch) {
        return { ivaFinal: tarifaOriginal, exoneracionMatch: rec };
      }
    }

    return { ivaFinal: tarifaOriginal, exoneracionMatch: null };
  };

  const agregarProductosSeleccionados = () => {
    const nuevasLineas = selectedProducts.map((producto, index) => {
      // Separar IVA de otros impuestos
      const impuestosProducto = producto.impuestos || [];
      const impuestoIva = impuestosProducto.find(i => i.codigoImpuesto === '01');
      const impuestosAdicionales = impuestosProducto.filter(i => i.codigoImpuesto !== '01');

      // Find matching exoneration for this product's CABYS
      const { ivaFinal: ivaLinea } = findExoneracionForProduct(
        impuestoIva?.porcentaje || producto.tarifaIva || tarifaDefault.porcentaje,
        producto.codigoCabys || '', receptor
      );

      return {
        id: Date.now() + index,
        cantidad: 1,
        detalle: producto.descripcion || producto.nombre,
        codigo: producto.codigoCabys || '',
        sku: producto.codigo || '',
        precioUnitario: producto.precio || 0,
        iva: ivaLinea,
        codigoTarifa: impuestoIva?.codigo || producto.codigoTarifa || tarifaDefault.codigo,
        impuestosAdicionales: impuestosAdicionales,
        descuento: 0
      };
    });

    setLineas(prev => [...prev, ...nuevasLineas]);

    const count = selectedProducts.length;
    setSelectedProducts([]);
    setProductSearchTerm('');
    setShowProductDropdown(false);
    setProductSearchResults([]);
    productInputRef.current?.focus();
    toast.success(`${count} productos agregados`);
  };

  // Select product from search results (single click)
  const seleccionarProducto = (producto) => {
    // Separar IVA de otros impuestos
    const impuestosProducto = producto.impuestos || [];
    const impuestoIva = impuestosProducto.find(i => i.codigoImpuesto === '01');
    const impuestosAdicionales = impuestosProducto.filter(i => i.codigoImpuesto !== '01');

    // Find matching exoneration for this product's CABYS
    const { ivaFinal, exoneracionMatch } = findExoneracionForProduct(
      impuestoIva?.porcentaje || producto.tarifaIva || tarifaDefault.porcentaje,
      producto.codigoCabys || '', receptor
    );

    agregarLinea({
      detalle: producto.descripcion || producto.nombre,
      codigo: producto.codigoCabys || '',
      sku: producto.codigo || '',
      precio: producto.precio || 0,
      iva: ivaFinal,
      codigoTarifa: impuestoIva?.codigo || producto.codigoTarifa || tarifaDefault.codigo,
      impuestosAdicionales: impuestosAdicionales,
      unidadMedida: producto.unidadMedida || 'Unid',
      moneda: producto.moneda || documento.moneda
    });
    setProductSearchTerm('');
    setShowProductDropdown(false);
    setProductSearchResults([]);
    setSelectedProducts([]);
    productInputRef.current?.focus();
  };

  // Debounced client search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (clientSearchTerm) {
        buscarCliente(clientSearchTerm);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearchTerm]);

  const seleccionarCliente = async (cliente) => {
    // Load full receptor data with exoneraciones
    try {
      const fullRes = await receptoresApi.getById(cliente.id);
      const full = fullRes.data?.data;
      if (full) {
        setReceptor({ ...cliente, ...full, exoneraciones: full.exoneraciones || null });
      } else {
        setReceptor(cliente);
      }
    } catch (e) {
      setReceptor(cliente);
    }
    setShowClientModal(false);
    setClientSearchTerm('');
    setClientSearchResults([]);

    // Auto-configure document based on client settings
    const clientMoneda = cliente.moneda_defecto || 'CRC';
    const clientDiasCredito = cliente.dias_credito || 0;
    setDocumento(prev => ({
      ...prev,
      tipoDocumento: cliente.genera_factura_electronica !== false ? '01' : '04', // FE or Tiquete
      moneda: clientMoneda,
      tipoCambio: clientMoneda === 'USD' ? (storeTipoCambio || 1) : 1,
      condicionVenta: cliente.condicion_venta_defecto || '01',
      diasCredito: clientDiasCredito
    }));

    toast.success(`Cliente: ${cliente.nombre}`);

    // Fetch activities from Hacienda
    setCargandoActividades(true);
    setActividadesCliente([]);
    try {
      const response = await receptoresApi.consultarHacienda(cliente.identificacion);
      if (response.data?.success && response.data?.data?.actividades) {
        setActividadesCliente(response.data.data.actividades);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setCargandoActividades(false);
    }
  };

  // Update client's main activity
  const actualizarActividadCliente = async (codigo, descripcion) => {
    if (!receptor?.id) return;

    try {
      await receptoresApi.updateActividad(receptor.id, {
        actividadEconomica: codigo,
        actividadEconomicaDescripcion: descripcion
      });

      setReceptor(prev => ({
        ...prev,
        actividad_economica: codigo,
        actividad_economica_descripcion: descripcion
      }));

      toast.success('Actividad económica actualizada');
    } catch (error) {
      console.error('Error updating activity:', error);
      toast.error('Error al actualizar actividad');
    }
  };

  const agregarLinea = (producto = null) => {
    const newId = Date.now();
    const nuevaLinea = producto ? {
      id: newId,
      cantidad: 1,
      detalle: producto.nombre || producto.detalle,
      codigo: producto.codigo || '',
      sku: producto.sku || '',
      precioUnitario: producto.precio || 0,
      iva: producto.iva || tarifaDefault.porcentaje,
      codigoTarifa: producto.codigoTarifa || tarifaDefault.codigo,
      impuestosAdicionales: producto.impuestosAdicionales || [],
      descuento: 0
    } : {
      id: newId,
      cantidad: 1,
      detalle: '',
      codigo: '',
      sku: '',
      precioUnitario: 0,
      iva: tarifaDefault.porcentaje,
      codigoTarifa: tarifaDefault.codigo,
      impuestosAdicionales: [],
      descuento: 0
    };

    setLineas([...lineas, nuevaLinea]);
    // Don't auto-select the new line
    return newId;
  };

  const eliminarLinea = (id) => {
    if (id) {
      // Eliminar una línea específica
      setLineas(lineas.filter(l => l.id !== id));
      setSelectedLineIds(prev => prev.filter(i => i !== id));
    } else if (selectedLineIds.length > 0) {
      // Eliminar todas las seleccionadas
      setLineas(lineas.filter(l => !selectedLineIds.includes(l.id)));
      setSelectedLineIds([]);
    }
  };

  const toggleLineSelection = (id) => {
    setSelectedLineIds(prev =>
      prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAllLines = () => {
    if (selectedLineIds.length === lineas.length) {
      setSelectedLineIds([]);
    } else {
      setSelectedLineIds(lineas.map(l => l.id));
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, lineaId) => {
    setDraggedLineId(lineaId);
    e.dataTransfer.effectAllowed = 'move';
    // Add visual feedback
    e.target.closest('.linea-row')?.classList.add('opacity-50');
  };

  const handleDragEnd = (e) => {
    setDraggedLineId(null);
    e.target.closest('.linea-row')?.classList.remove('opacity-50');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetLineaId) => {
    e.preventDefault();

    if (!draggedLineId || draggedLineId === targetLineaId) {
      setDraggedLineId(null);
      return;
    }

    // Reorder lines
    const draggedIndex = lineas.findIndex(l => l.id === draggedLineId);
    const targetIndex = lineas.findIndex(l => l.id === targetLineaId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedLineId(null);
      return;
    }

    const newLineas = [...lineas];
    const [draggedItem] = newLineas.splice(draggedIndex, 1);
    newLineas.splice(targetIndex, 0, draggedItem);

    setLineas(newLineas);
    setDraggedLineId(null);
  };

  const actualizarLinea = (id, campo, valor) => {
    setLineas(lineas.map(l => l.id === id ? { ...l, [campo]: valor } : l));
  };

  // Cambiar tarifa de IVA para TODAS las líneas de la factura
  const cambiarTarifaTodasLineas = (codigoTarifa) => {
    const tarifa = tarifasIva.find(t => t.codigo === codigoTarifa);
    if (!tarifa) return;

    setLineas(lineas.map(l => ({
      ...l,
      iva: tarifa.porcentaje,
      codigoTarifa: tarifa.codigo
    })));
  };

  // Handle product search/add via Enter and arrow navigation
  const handleProductSearch = (e) => {
    const resultsCount = productSearchResults.length;

    // Arrow Down - move highlight down
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (resultsCount > 0) {
        setHighlightedIndex(prev => (prev < resultsCount - 1 ? prev + 1 : 0));
      }
      return;
    }

    // Arrow Up - move highlight up
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (resultsCount > 0) {
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : resultsCount - 1));
      }
      return;
    }

    // Escape - close dropdown
    if (e.key === 'Escape') {
      setShowProductDropdown(false);
      setHighlightedIndex(-1);
      return;
    }

    // Enter - select highlighted or add manual
    if (e.key === 'Enter' && productSearchTerm.trim()) {
      e.preventDefault();

      // If a product is highlighted, select it
      if (highlightedIndex >= 0 && highlightedIndex < resultsCount) {
        seleccionarProducto(productSearchResults[highlightedIndex]);
        setHighlightedIndex(-1);
        return;
      }

      // No results - do nothing on Enter (user should click "Crear nuevo producto")
      // Don't add manual entry since products require CABYS, taxes, etc.
    }
  };

  const calcularSubtotal = () => {
    return lineas.reduce((sum, l) => {
      const subtotal = l.cantidad * l.precioUnitario;
      const descuentoMonto = subtotal * ((l.descuento || 0) / 100);
      return sum + (subtotal - descuentoMonto);
    }, 0);
  };

  const calcularDescuento = () => {
    return lineas.reduce((sum, l) => {
      const subtotal = l.cantidad * l.precioUnitario;
      return sum + (subtotal * ((l.descuento || 0) / 100));
    }, 0);
  };

  // Verificar si una línea tiene exoneración aplicable
  const lineaTieneExoneracion = (linea) => {
    // NC ('03') y ND ('02') NO aplican exoneración del receptor — replican la FE
    // original. Si la FE no tenía exoneración, la NC tampoco debe tenerla, sino
    // Hacienda rechaza con -106/-108 (línea sin <Exoneracion> pero totales con
    // exonerado). Si la línea ya viene con exoneración propia (heredada de la FE
    // original), eso lo lleva la línea misma — acá solo controlamos la del
    // receptor que se aplicaría como default a líneas nuevas.
    if (documento.tipoDocumento === '03' || documento.tipoDocumento === '02') return false;

    // Si hay exoneración temporal, aplica a todas las líneas
    if (exoneracionTemporal) return true;

    const exoneracionActiva = receptor?.exoneracionActiva || receptor?.exoneracion_activa;
    if (!exoneracionActiva) return false;

    const cabysAutorizados = receptor?.exoneracionCabys || receptor?.exoneracion_cabys;

    // Si no hay CABYS específicos, la exoneración aplica a todo
    if (!cabysAutorizados || cabysAutorizados.length === 0) return true;

    // Verificar si el CABYS de la línea está en los autorizados
    const cabysLinea = linea.codigo || '';
    if (!cabysLinea) return false;

    // Parsear cabysAutorizados si es string JSON
    let cabysArray = cabysAutorizados;
    if (typeof cabysAutorizados === 'string') {
      try { cabysArray = JSON.parse(cabysAutorizados); } catch { return false; }
    }

    return cabysArray.some(cabysAuth => cabysLinea === cabysAuth);
  };

  // Obtener el porcentaje de exoneración (temporal o del receptor)
  const getPorcentajeExoneracion = () => {
    if (exoneracionTemporal) {
      return exoneracionTemporal.porcentaje || 13;
    }
    return receptor?.exoneracionPorcentaje || receptor?.exoneracion_porcentaje || 0;
  };

  // Verificar si hay exoneración activa con datos válidos (temporal o del receptor)
  const tieneExoneracionActiva = () => {
    if (exoneracionTemporal) return true;
    const porcentaje = receptor?.exoneracionPorcentaje || receptor?.exoneracion_porcentaje;
    return !!(receptor?.exoneracionActiva || receptor?.exoneracion_activa) && porcentaje && porcentaje > 0;
  };

  // Obtener datos de exoneración para el XML.
  // Prioridad: exoneración temporal → primera de receptor.exoneraciones[] (tiene campos
  // legales completos: articulo, inciso, institucionCodigo) → columnas legacy del receptor.
  const getExoneracionData = () => {
    if (exoneracionTemporal) {
      return {
        tipoDocumento: exoneracionTemporal.tipoDocumento,
        tipoDocumentoDesc: exoneracionTemporal.tipoDocumentoDesc,
        numeroDocumento: exoneracionTemporal.numeroDocumento,
        nombreInstitucion: exoneracionTemporal.nombreInstitucion,
        articulo: exoneracionTemporal.articuloLey || exoneracionTemporal.articulo,
        inciso: exoneracionTemporal.incisoLey || exoneracionTemporal.inciso,
        fechaEmision: exoneracionTemporal.fechaEmision,
        porcentaje: exoneracionTemporal.porcentaje
      };
    }
    const exoArr = receptor?.exoneraciones || [];
    const exoNueva = exoArr.length > 0 ? exoArr[0] : null;
    return {
      tipoDocumento: exoNueva?.tipoDocumento || receptor?.exoneracionTipoDoc || receptor?.exoneracion_tipo_doc || '04',
      tipoDocumentoDesc: exoNueva?.tipoDocumentoDesc || receptor?.exoneracionTipoDocDesc || receptor?.exoneracion_tipo_doc_desc || 'Exoneración',
      numeroDocumento: exoNueva?.numeroDocumento || receptor?.exoneracionNumero || receptor?.exoneracion_numero,
      // Código de institución (XML <NombreInstitucion> espera el CÓDIGO de 2 dígitos, ej '16').
      // Sin valor → XMLGenerator rebota con 400 (ya no más default silencioso '01').
      nombreInstitucion: exoNueva?.institucionCodigo || receptor?.exoneracionInstitucionCodigo || receptor?.exoneracion_institucion_codigo,
      articulo: exoNueva?.articuloLey,
      inciso: exoNueva?.incisoLey,
      fechaEmision: exoNueva?.fechaEmision || receptor?.exoneracionFechaEmision || receptor?.exoneracion_fecha_emision,
      porcentaje: exoNueva?.porcentaje || receptor?.exoneracionPorcentaje || receptor?.exoneracion_porcentaje || 13
    };
  };

  const calcularIVA = () => {
    return lineas.reduce((sum, l) => {
      const subtotal = l.cantidad * l.precioUnitario;
      const descuentoMonto = subtotal * ((l.descuento || 0) / 100);
      const base = subtotal - descuentoMonto;
      const ivaOriginal = base * (l.iva / 100);

      // Aplicar exoneración si corresponde
      if (lineaTieneExoneracion(l)) {
        const porcentajeExoneracion = getPorcentajeExoneracion();
        // La exoneración representa los puntos porcentuales de IVA exonerados
        // Ej: IVA 13%, Exoneración 13% -> IVA final 0%
        // Ej: IVA 13%, Exoneración 8% -> IVA final ~5%
        const ivaExonerado = base * (Math.min(porcentajeExoneracion, l.iva) / 100);
        return sum + (ivaOriginal - ivaExonerado);
      }

      return sum + ivaOriginal;
    }, 0);
  };

  // Calcular monto exonerado total
  const calcularMontoExonerado = () => {
    return lineas.reduce((sum, l) => {
      if (!lineaTieneExoneracion(l)) return sum;

      const subtotal = l.cantidad * l.precioUnitario;
      const descuentoMonto = subtotal * ((l.descuento || 0) / 100);
      const base = subtotal - descuentoMonto;
      const porcentajeExoneracion = getPorcentajeExoneracion();

      // El monto exonerado es la base * los puntos porcentuales exonerados (máximo el IVA)
      return sum + (base * (Math.min(porcentajeExoneracion, l.iva) / 100));
    }, 0);
  };

  // Calcular impuestos adicionales (911, Cruz Roja)
  const calcularImpuestosAdicionales = () => {
    const totales = {};
    lineas.forEach(l => {
      if (!l.impuestosAdicionales || l.impuestosAdicionales.length === 0) return;

      const subtotal = l.cantidad * l.precioUnitario;
      const descuentoMonto = subtotal * ((l.descuento || 0) / 100);
      const base = subtotal - descuentoMonto;

      l.impuestosAdicionales.forEach(imp => {
        const monto = base * (imp.porcentaje / 100);
        const key = `${imp.codigoImpuesto}-${imp.codigo}`;
        if (!totales[key]) {
          totales[key] = {
            codigoImpuesto: imp.codigoImpuesto,
            codigo: imp.codigo,
            descripcion: imp.descripcion,
            porcentaje: imp.porcentaje,
            monto: 0
          };
        }
        totales[key].monto += monto;
      });
    });
    return Object.values(totales);
  };

  const calcularTotalImpuestosAdicionales = () => {
    return calcularImpuestosAdicionales().reduce((sum, imp) => sum + imp.monto, 0);
  };

  const calcularTotal = () => {
    return calcularSubtotal() + calcularIVA() + calcularTotalImpuestosAdicionales();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: documento.moneda === 'USD' ? 'USD' : 'CRC',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const nuevaFactura = () => {
    const ejecutar = () => {
      setReceptor(null);
      setLineas([]);
      setDocumento({
        tipoDocumento: '01',
        condicionVenta: '02',
        medioPago: '01',
        moneda: 'USD',
        tipoCambio: storeTipoCambio || 1,
        comentarios: ''
      });
      setSelectedLineIds([]);
      setDocumentoId(null);
      setEsRecurrente(false); setRecurringConfig(null);
      setIsRejectedEdit(false);
      setRejectedConsecutivo(null);
      localStorage.removeItem('factura_en_progreso');
      toast.success('Nueva factura iniciada');
    };

    if (lineas.length > 0) {
      showConfirm(
        '¿Descartar factura actual?',
        'Se perderán todos los datos ingresados que no hayan sido guardados.',
        ejecutar,
        'warning'
      );
    } else {
      ejecutar();
    }
  };

  const handleGuardar = async (enviar = false, skipExoneracionValidation = false, skipCreditCheck = false, condicionOverride = null) => {
    if (documento.tipoDocumento !== '04' && !receptor) {
      toast.error('Debe asignar un cliente');
      return;
    }

    if (lineas.length === 0) {
      toast.error('Agregue al menos un producto/servicio');
      return;
    }

    if (lineas.some(l => !l.detalle || l.precioUnitario <= 0)) {
      toast.error('Complete todas las líneas de detalle');
      return;
    }

    // Use override if provided (avoids async setState race condition)
    const condicionVenta = condicionOverride || documento.condicionVenta;

    // Validar exoneración si el cliente tiene una activa (solo para FE y TE)
    if (!skipExoneracionValidation && (receptor?.exoneracionActiva || receptor?.exoneracion_activa)) {
      try {
        const validationResponse = await documentsApi.validateExoneracion(
          receptor.id,
          lineas.map(l => ({
            cabys: l.codigo,
            descripcion: l.detalle,
            cantidad: l.cantidad,
            precioUnitario: l.precioUnitario,
            total: parseFloat(l.cantidad) * parseFloat(l.precioUnitario)
          }))
        );

        if (validationResponse.data?.success && validationResponse.data?.clienteExonerado) {
          const validation = validationResponse.data;

          // Si la exoneración está vencida, solo advertir pero permitir continuar
          if (validation.exoneracionVencida) {
            toast.error('La exoneración del cliente está vencida. El IVA se aplicará normalmente.');
          }
          // Si hay líneas sin match y la exoneración tiene CABYS específicos
          else if (!validation.aplicaATodo && validation.lineasSinMatch?.length > 0) {
            setExoneracionValidation(validation);
            setPendingEnviar(enviar);
            setShowExoneracionModal(true);
            return; // Detener y mostrar modal
          }
        }
      } catch (error) {
        console.error('Error validando exoneración:', error);
        // Continuar sin bloquear si hay error en validación
      }
    }

    setLoading(true);
    try {
      const data = {
        tipoDocumento: documento.tipoDocumento,
        // Solo enviar el ID del cliente - NUNCA modificar datos del cliente al crear factura
        receptorId: receptor?.id || null,
        condicionVenta,
        medioPago: [documento.medioPago],
        codigoMoneda: documento.moneda,
        tipoCambio: documento.moneda === 'USD' ? (documento.tipoCambio || 1) : 1,
        plazoCredito: documento.diasCredito || null,
        // metadata: si hay config recurrente, la guardamos en pendingRecurring
        //          para que el statusChecker cree la plantilla al aceptarse en Hacienda.
        metadata: (() => {
          const m = {};
          if (documento.ordenCompra) m.ordenCompra = documento.ordenCompra;
          if (recurringConfig) m.pendingRecurring = recurringConfig;
          return Object.keys(m).length > 0 ? m : null;
        })(),
        esRecurrente: !!recurringConfig,
        recurringConfig: recurringConfig || null,
        lineas: lineas.map((l, idx) => {
          const tieneExo = lineaTieneExoneracion(l);
          const subtotal = parseFloat(l.cantidad) * parseFloat(l.precioUnitario);
          const descuentoMonto = subtotal * ((l.descuento || 0) / 100);
          const base = subtotal - descuentoMonto;

          const lineaData = {
            numeroLinea: idx + 1,
            cantidad: parseFloat(l.cantidad),
            detalle: l.detalle,
            codigo: l.codigo || undefined,
            codigoComercial: l.sku || undefined,
            precioUnitario: parseFloat(l.precioUnitario),
            descuento: l.descuento ? descuentoMonto : 0,
            descuentoPorcentaje: l.descuento || 0,
            impuesto: {
              codigo: '01',
              codigoTarifa: l.codigoTarifa || '08',
              tarifa: l.iva
            },
            tipoCambio: documento.moneda === 'USD' ? (documento.tipoCambio || 1) : 1
          };

          // Agregar impuestos adicionales (911, Cruz Roja)
          if (l.impuestosAdicionales && l.impuestosAdicionales.length > 0) {
            lineaData.impuestosAdicionales = l.impuestosAdicionales.map(imp => ({
              codigo: imp.codigoImpuesto,
              codigoTarifa: imp.codigo,
              tarifa: imp.porcentaje,
              monto: base * (imp.porcentaje / 100)
            }));
          }

          // Agregar exoneración si la línea tiene exoneración aplicable
          if (tieneExo) {
            const exoData = getExoneracionData();
            const porcentajeExo = exoData.porcentaje;
            const montoExonerado = base * (Math.min(porcentajeExo, l.iva) / 100);

            lineaData.impuestoExoneracion = {
              tipoDocumento: exoData.tipoDocumento,
              tipoDocumentoDesc: exoData.tipoDocumentoDesc,
              numeroDocumento: exoData.numeroDocumento,
              nombreInstitucion: exoData.nombreInstitucion,
              articulo: exoData.articulo,
              inciso: exoData.inciso,
              fechaEmision: exoData.fechaEmision,
              porcentaje: porcentajeExo,
              monto: montoExonerado
            };
          }

          return lineaData;
        }),
        notas: documento.comentarios,
        periodoCobroTexto: armarPeriodoCobro(periodoCobro)
      };

      // Si hay un documento cargado, actualizar en lugar de crear
      if (documentoId) {
        await documentsApi.update(documentoId, data);

        if (enviar) {
          // Después de actualizar, enviar a Hacienda
          await documentsApi.submit(documentoId);
          if (isRejectedEdit) {
            toast.success('Documento corregido y reenviado a Hacienda');
          } else {
            toast.success('Factura enviada a Hacienda');
          }
        } else {
          toast.success('Borrador actualizado');
        }
      } else {
        // Crear nuevo documento
        await documentsApi.create(data, enviar);

        if (enviar) {
          toast.success('Factura enviada a Hacienda');
        } else {
          toast.success('Borrador guardado');
        }
      }

      // Limpiar formulario después de guardar
      setReceptor(null);
      setLineas([]);
      setDocumento({
        tipoDocumento: '01',
        condicionVenta: '02',
        medioPago: '01',
        moneda: 'USD',
        tipoCambio: storeTipoCambio || 1,
        comentarios: ''
      });
      setSelectedLineIds([]);
      setDocumentoId(null);
      setEsRecurrente(false); setRecurringConfig(null);
      setExoneracionTemporal(null);
      setIsRejectedEdit(false);
      setRejectedConsecutivo(null);
      localStorage.removeItem('factura_en_progreso');
      localStorage.removeItem('exoneracionTemporal');

    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] flex flex-col -mx-4 lg:-mx-6 -mt-4 lg:-mt-6 overflow-x-auto">
      {/* Banner para documento rechazado */}
      {isRejectedEdit && (
        <div className="bg-red-600 text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <XCircle className="h-5 w-5" />
            <div>
              <span className="font-semibold">Editando documento rechazado</span>
              {rejectedConsecutivo && (
                <span className="ml-2 text-red-200">• Consecutivo: {rejectedConsecutivo}</span>
              )}
            </div>
          </div>
          <div className="text-sm text-red-200">
            Corrija los errores y haga clic en "Enviar a Hacienda" para reenviar con el mismo consecutivo
          </div>
        </div>
      )}
      {/* Toolbar - Responsive */}
      <div className="bg-white border-b border-gray-200 py-2 relative">
        <style>{`
          .toolbar-container {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
            gap: clamp(2px, 0.3vw, 6px);
            padding: 0 clamp(4px, 1vw, 16px);
          }
          .toolbar-btn {
            min-width: clamp(45px, 5vw, 70px);
            padding: clamp(4px, 0.5vw, 8px) clamp(4px, 0.6vw, 12px);
          }
          .toolbar-icon {
            width: clamp(14px, 1.5vw, 20px);
            height: clamp(14px, 1.5vw, 20px);
          }
          .toolbar-label {
            font-size: clamp(8px, 0.9vw, 12px);
          }
          .toolbar-separator {
            height: clamp(24px, 3vw, 40px);
            margin: 0 clamp(2px, 0.3vw, 4px);
            flex-shrink: 0;
          }
        `}</style>
        <div className="toolbar-container">
          {/* Facturas */}
          <ToolbarButton
            icon={FileText}
            label="Nueva Factura"
            onClick={nuevaFactura}
            variant="primary"
          />
          <ToolbarButton icon={Eye} label="Pre-visualizar" onClick={() => setShowPreview(true)} variant="purple" />
          <ToolbarButton icon={FileText} label="Recuperar Factura" onClick={() => navigate('/facturacion/seleccionar?tipo=facturas')} variant="primary" />

          <ToolbarSeparator />

          {/* Clientes */}
          <ToolbarButton
            icon={User}
            label="Asignar Cliente"
            onClick={() => setShowClientModal(true)}
            active={!!receptor}
            variant={receptor ? 'success' : 'teal'}
          />

          <ToolbarSeparator />

          {/* Items */}
          <ToolbarButton
            icon={Delete}
            label="Eliminar Item"
            onClick={() => eliminarLinea()}
            disabled={selectedLineIds.length === 0}
            variant="rose"
          />

          <ToolbarSeparator />

          {/* Guardar */}
          <ToolbarButton
            icon={Save}
            label="Guardar"
            onClick={() => handleGuardar(false)}
            disabled={loading || lineas.length === 0}
            variant="sky"
          />

          <ToolbarSeparator />

          <ToolbarButton
            icon={MessageSquare}
            label="Comentarios"
            onClick={() => setShowComments(true)}
            active={!!documento.comentarios}
            variant="slate"
          />
          <ToolbarButton
            icon={Settings}
            label="Opciones"
            onClick={() => setShowOpcionesFactura(true)}
            active={!!documento.ordenCompra || documento.diasCredito !== 30 || periodoCobro.activo}
            variant="indigo"
          />

          <ToolbarSeparator />

          <ToolbarButton
            icon={RefreshCw}
            label={recurringConfig ? 'Recurrente ✓' : 'Recurrente'}
            onClick={() => setShowRecurrenteModal(true)}
            active={!!recurringConfig}
            variant="cyan"
          />

        </div>
      </div>

      {/* Banner de plantilla recurrente configurada */}
      {recurringConfig && (() => {
        const FREC = { weekly: 'Semanal', biweekly: 'Quincenal', monthly: 'Mensual', quarterly: 'Trimestral', yearly: 'Anual' };
        const dias = (recurringConfig.diasGeneracion || []).join(', ');
        const modo = recurringConfig.generaComo === 'pending' ? 'Envío automático' : 'Borrador';
        return (
          <div className="mb-4 px-4 py-3 bg-gradient-to-r from-cyan-50 to-blue-50 border-2 border-cyan-300 rounded-lg flex items-center justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <RefreshCw className="h-5 w-5 text-cyan-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-semibold text-cyan-800">Esta factura se convertirá en Plantilla Recurrente al ser aceptada por Hacienda</div>
                <div className="text-cyan-700 mt-0.5">
                  <strong>{FREC[recurringConfig.frecuencia] || recurringConfig.frecuencia}</strong>
                  {dias && <> · Día(s): <strong>{dias}</strong></>}
                  {' · Próxima: '}<strong>{recurringConfig.proximaGeneracion}</strong>
                  {' · Modo: '}<strong>{modo}</strong>
                  {recurringConfig.nombre && <> · "{recurringConfig.nombre}"</>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowRecurrenteModal(true)}
                className="px-3 py-1.5 text-xs font-medium text-cyan-700 border border-cyan-300 rounded hover:bg-cyan-100"
              >
                Editar
              </button>
              <button
                type="button"
                onClick={() => setRecurringConfig(null)}
                className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 rounded hover:bg-red-50"
              >
                Quitar
              </button>
            </div>
          </div>
        );
      })()}

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Left panel - Items */}
        <div className="flex-1 flex flex-col bg-gray-50 p-4 min-w-0">
          {/* Product search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                ref={productInputRef}
                type="text"
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                onKeyDown={handleProductSearch}
                onFocus={() => productSearchResults.length > 0 && setShowProductDropdown(true)}
                placeholder="Buscar producto por nombre, código o CABYS..."
                className="w-full pl-10 pr-10 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-0 outline-none"
              />
              {searchingProducts && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                </div>
              )}

              {/* Product search dropdown */}
              {showProductDropdown && productSearchResults.length > 0 && (
                <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden flex flex-col">
                  {/* Header with add selected button */}
                  {selectedProducts.length > 0 && (
                    <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center justify-between sticky top-0">
                      <span className="text-sm text-blue-700 font-medium">
                        {selectedProducts.length} producto{selectedProducts.length > 1 ? 's' : ''} seleccionado{selectedProducts.length > 1 ? 's' : ''}
                      </span>
                      <button
                        type="button"
                        onClick={agregarProductosSeleccionados}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Agregar todos
                      </button>
                    </div>
                  )}
                  {/* Product list */}
                  <div ref={productListRef} className="overflow-y-auto flex-1">
                    {productSearchResults.map((producto, index) => {
                      const isSelected = selectedProducts.some(p => p.id === producto.id);
                      const isHighlighted = index === highlightedIndex;
                      return (
                        <div
                          key={producto.id}
                          className={`flex items-center px-4 py-3 border-b border-gray-100 last:border-0 transition-colors ${
                            isHighlighted ? 'bg-blue-100 border-l-4 border-l-blue-500' : isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          {/* Checkbox */}
                          <div className="mr-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleProductSelection(producto)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </div>
                          {/* Product info - click to add single */}
                          <button
                            type="button"
                            onClick={() => seleccionarProducto(producto)}
                            className="flex-1 text-left"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-gray-500 font-mono">{producto.codigo}</div>
                                <div className="font-medium text-gray-900">{producto.descripcion || producto.nombre}</div>
                                <div className="text-xs text-gray-500 flex flex-wrap gap-2 mt-1">
                                  {producto.codigoCabys && (
                                    <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-mono">
                                      {producto.codigoCabys}
                                    </span>
                                  )}
                                  <span>IVA: {producto.tarifaIva}%</span>
                                  {producto.impuestos?.filter(i => i.codigoImpuesto !== '01').map(imp => (
                                    <span
                                      key={imp.id || imp.codigo}
                                      className={`px-1.5 py-0.5 rounded font-medium ${
                                        imp.codigoImpuesto === '07'
                                          ? 'bg-orange-100 text-orange-700'
                                          : 'bg-red-100 text-red-700'
                                      }`}
                                    >
                                      {imp.codigoImpuesto === '07' ? '911' : 'Cruz Roja'}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div className="text-right ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {producto.moneda === 'USD' ? '$' : '₡'}{producto.precio?.toLocaleString('es-CR')}
                                </div>
                              </div>
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No results message */}
              {showProductDropdown && productSearchTerm.length >= 2 && !searchingProducts && productSearchResults.length === 0 && (
                <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                  <p className="text-center text-gray-500 mb-3">No se encontraron productos</p>
                  <button
                    type="button"
                    onClick={() => {
                      setNuevoProductoNombre(productSearchTerm);
                      setShowNuevoProductoModal(true);
                      setShowProductDropdown(false);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Crear nuevo producto
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Click outside to close dropdown */}
          {showProductDropdown && (
            <div
              className="fixed inset-0 z-40"
              onClick={() => {
                setShowProductDropdown(false);
                setSelectedProducts([]);
              }}
            />
          )}

          {/* Items table */}
          <div className="flex-1 bg-white rounded-lg overflow-x-auto flex flex-col min-h-0">
            {/* Header con borde redondeado */}
            <div className="border-2 border-gray-800 rounded-t-lg min-w-[1050px]">
              <div className="flex items-center py-2 px-2">
                {/* Spacer for drag handle column */}
                <div className="w-6"></div>
                <div className="w-8 flex justify-center">
                  <input
                    type="checkbox"
                    checked={lineas.length > 0 && selectedLineIds.length === lineas.length}
                    onChange={toggleSelectAllLines}
                    className="rounded border-gray-400"
                  />
                </div>
                <div className="w-36 text-left text-sm font-semibold text-gray-700 px-2">CABYS</div>
                <div className="w-24 text-left text-sm font-semibold text-gray-700 px-2">Código</div>
                <div className="flex-1 min-w-[250px] text-left text-sm font-semibold text-gray-700 px-2">Descripción</div>
                <div className="w-20 text-right text-sm font-semibold text-gray-700 px-2">Cantidad</div>
                <div className="w-28 text-right text-sm font-semibold text-gray-700 px-2">Precio</div>
                <div className="w-20 text-right text-sm font-semibold text-gray-700 px-2">Descuento</div>
                <div className="w-24 text-right text-sm font-semibold text-gray-700 px-2">IVA 13%</div>
                <div className="w-28 text-right text-sm font-semibold text-gray-700 px-2">Total</div>
              </div>
            </div>
            {/* Body - scrollable */}
            <div className="border-x border-b border-gray-200 divide-y divide-gray-100 text-sm overflow-y-auto flex-1 min-h-0 min-w-[1050px]">
              {lineas.length === 0 ? (
                <div className="py-12 text-center text-gray-400">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No hay productos agregados</p>
                  <p className="text-sm">Use la barra de búsqueda para agregar productos</p>
                </div>
              ) : (
                lineas.map((linea) => {
                  const subtotal = linea.cantidad * linea.precioUnitario;
                  const descuentoMonto = subtotal * ((linea.descuento || 0) / 100);
                  const baseIva = subtotal - descuentoMonto;
                  const ivaBruto = baseIva * (linea.iva / 100);
                  const tieneExoneracion = lineaTieneExoneracion(linea);
                  const montoExoneradoLinea = tieneExoneracion ? baseIva * (Math.min(getPorcentajeExoneracion(), linea.iva) / 100) : 0;
                  const ivaMonto = ivaBruto - montoExoneradoLinea; // IVA neto después de exoneración
                  const total = baseIva + ivaMonto;

                  return (
                    <div
                      key={linea.id}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, linea.id)}
                      className={`linea-row flex items-start pt-2 pb-1.5 px-2 transition-colors border-b border-gray-100 last:border-b-0 ${selectedLineIds.includes(linea.id) ? 'bg-blue-50' : 'hover:bg-gray-50'} ${draggedLineId === linea.id ? 'opacity-50 bg-blue-100' : ''} ${draggedLineId && draggedLineId !== linea.id ? 'border-t-2 border-transparent hover:border-blue-400' : ''}`}
                    >
                      {/* Drag handle - only this is draggable */}
                      <div
                        draggable
                        onDragStart={(e) => handleDragStart(e, linea.id)}
                        onDragEnd={handleDragEnd}
                        className="w-6 flex justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                      >
                        <GripVertical className="h-4 w-4" />
                      </div>
                      <div className="w-8 flex justify-center">
                        <input
                          type="checkbox"
                          checked={selectedLineIds.includes(linea.id)}
                          onChange={() => toggleLineSelection(linea.id)}
                          className="rounded border-gray-300 cursor-pointer"
                        />
                      </div>
                      <div className="w-36 px-2">
                        <input
                          type="text"
                          value={linea.codigo}
                          onChange={(e) => actualizarLinea(linea.id, 'codigo', e.target.value)}
                          className="w-full px-1.5 py-1 border border-gray-300 rounded text-xs font-mono"
                          placeholder="CABYS"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="w-24 px-2">
                        <input
                          type="text"
                          value={linea.sku || ''}
                          onChange={(e) => actualizarLinea(linea.id, 'sku', e.target.value)}
                          className="w-full px-1.5 py-1 border border-gray-300 rounded text-xs font-mono"
                          placeholder="Código"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="flex-1 min-w-[250px] px-2">
                        <textarea
                          value={linea.detalle}
                          onChange={(e) => actualizarLinea(linea.id, 'detalle', e.target.value)}
                          className="w-full px-1.5 py-1 border border-gray-300 rounded text-sm resize-none min-h-[32px]"
                          placeholder="Descripción del producto/servicio"
                          rows={Math.max(1, (linea.detalle || '').split('\n').length)}
                          ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                        />
                        {tieneExoneracion && (() => {
                          const { exoneracionMatch } = findExoneracionForProduct(13, linea.codigo || '', receptor);
                          // Si el match es un objeto de exoneración (del array exoneraciones[]) → tiene numeroDocumento.
                          // Si el match es el receptor entero (fallback legacy) o null → usar getExoneracionData()
                          // que normaliza los campos legacy (receptor.exoneracionNumero → numeroDocumento).
                          const matchIsExoneracion = exoneracionMatch && exoneracionMatch.numeroDocumento;
                          const exoData = matchIsExoneracion ? exoneracionMatch : getExoneracionData();
                          const exoNum = exoData.numeroDocumento || 'sin documento';
                          const exoDesc = exoData.tipoDocumentoDesc || 'Exoneración';
                          const exoPct = matchIsExoneracion ? exoneracionMatch.porcentaje : getPorcentajeExoneracion();
                          return (
                            <div className="mt-1 px-2 py-1 bg-green-50 rounded text-[10px] text-green-700 break-words">
                              <span className="font-semibold">Exoneración {exoPct}%:</span> {formatCurrency(montoExoneradoLinea)} · {exoDesc} · Doc. {exoNum}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="w-20 px-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={linea.cantidad}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || val.endsWith('.') || val.endsWith('.0')) {
                              actualizarLinea(linea.id, 'cantidad', val);
                            } else {
                              actualizarLinea(linea.id, 'cantidad', parseFloat(val) || 1);
                            }
                          }}
                          onBlur={(e) => actualizarLinea(linea.id, 'cantidad', parseFloat(e.target.value) || 1)}
                          className="w-full px-1.5 py-1 border border-gray-300 rounded text-right text-sm"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="w-28 px-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          data-line-price={linea.id}
                          value={linea.precioUnitario}
                          onChange={(e) => {
                            const val = e.target.value;
                            // Allow typing decimal point - store as string temporarily
                            if (val === '' || val === '.' || val.endsWith('.') || val.endsWith('.0') || val.endsWith('.00')) {
                              actualizarLinea(linea.id, 'precioUnitario', val);
                            } else {
                              actualizarLinea(linea.id, 'precioUnitario', parseFloat(val) || 0);
                            }
                          }}
                          onBlur={(e) => actualizarLinea(linea.id, 'precioUnitario', parseFloat(e.target.value) || 0)}
                          className="w-full px-1.5 py-1 border border-gray-300 rounded text-right text-sm"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="w-20 px-2">
                        <div className="flex items-center">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={linea.descuento || 0}
                            onChange={(e) => actualizarLinea(linea.id, 'descuento', parseFloat(e.target.value) || 0)}
                            className="w-full px-1.5 py-1 border border-gray-300 rounded text-right text-sm"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="ml-1 text-gray-500 text-xs">%</span>
                        </div>
                      </div>
                      <div className="w-24 px-2 text-right">
                        <div className="text-sm text-gray-600">{formatCurrency(ivaMonto)}</div>
                      </div>
                      <div className="w-28 px-2 text-right">
                        <div className="font-semibold text-gray-900">{formatCurrency(total)}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Right panel - Client & Totals */}
        <div className="w-full lg:w-80 bg-white border-t lg:border-t-0 lg:border-l border-gray-200 flex flex-col shrink-0">
          {/* Client info */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900">Cliente</h3>
              <button
                onClick={() => setShowClientModal(true)}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                {receptor ? 'Cambiar' : 'Asignar'}
              </button>
            </div>
            {receptor ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{receptor.nombre}</p>
                    <p className="text-sm text-gray-600">{receptor.identificacion}</p>
                  </div>
                  {(() => {
                    const porcentaje = exoneracionTemporal?.porcentaje || receptor.exoneracionPorcentaje || receptor.exoneracion_porcentaje;
                    // Solo mostrar badge si hay exoneración temporal O si el cliente tiene exoneración con porcentaje válido
                    const tieneExoneracion = exoneracionTemporal || (
                      (receptor.exoneracionActiva || receptor.exoneracion_activa) && porcentaje && porcentaje > 0
                    );

                    if (!tieneExoneracion) return null;

                    const fechaVenc = receptor.exoneracionFechaVencimiento || receptor.exoneracion_fecha_vencimiento;
                    const vencido = !exoneracionTemporal && fechaVenc && new Date(fechaVenc) < new Date();

                    return (
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                        vencido
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        <Shield className="h-3 w-3" />
                        {vencido ? 'VENCIDO' : `EXONERADO ${porcentaje}%`}
                      </div>
                    );
                  })()}
                </div>
                {receptor.email && <p className="text-sm text-gray-500">{receptor.email}</p>}

                {/* Info de exoneración si está activa con datos válidos */}
                {(() => {
                  const porcentaje = exoneracionTemporal?.porcentaje || receptor.exoneracionPorcentaje || receptor.exoneracion_porcentaje;
                  const tieneExoneracionValida = exoneracionTemporal || (
                    (receptor.exoneracionActiva || receptor.exoneracion_activa) && porcentaje && porcentaje > 0
                  );

                  if (tieneExoneracionValida) {
                    return (
                      <div className="mt-2 pt-2 border-t border-green-200 space-y-1">
                        {receptor.exoneraciones && receptor.exoneraciones.length > 0 ? (
                          receptor.exoneraciones.map((exo, idx) => (
                            <p key={idx} className="text-xs text-purple-600">
                              <span className="font-medium">Exoneración:</span> {exo.numeroDocumento}
                              {exo.fechaVencimiento && <span className="text-gray-500 ml-1">Vence: {new Date(exo.fechaVencimiento).toLocaleDateString('es-CR')}</span>}
                            </p>
                          ))
                        ) : (
                          <p className="text-xs text-purple-600">
                            <span className="font-medium">Exoneración:</span> {exoneracionTemporal?.numeroDocumento || receptor.exoneracionNumero || receptor.exoneracion_numero}
                          </p>
                        )}
                        {(exoneracionTemporal || receptor.exoneracionFechaVencimiento || receptor.exoneracion_fecha_vencimiento) && !receptor.exoneraciones?.length && (
                          <p className="text-xs text-gray-500">
                            {exoneracionTemporal ? (
                              <span className="text-purple-600 font-medium">(Solo para esta factura)</span>
                            ) : (
                              <>Vence: {new Date(receptor.exoneracionFechaVencimiento || receptor.exoneracion_fecha_vencimiento).toLocaleDateString('es-CR')}</>
                            )}
                          </p>
                        )}
                        {exoneracionTemporal && (
                          <button
                            onClick={() => setExoneracionTemporal(null)}
                            className="mt-1 text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                          >
                            <X className="h-3 w-3" />
                            Quitar exoneración
                          </button>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div className="mt-2 pt-2 border-t border-green-200">
                      <button
                        onClick={() => setShowExoneracionRapidaModal(true)}
                        className="w-full text-xs px-2 py-1.5 border border-purple-300 text-purple-600 rounded hover:bg-purple-50 flex items-center justify-center gap-1 transition-colors"
                      >
                        <Shield className="h-3 w-3" />
                        Agregar Exoneración
                      </button>
                    </div>
                  );
                })()}

                {/* Actividad Económica */}
                <div className="mt-2 pt-2 border-t border-green-200">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs text-gray-500">Actividad Económica</label>
                    {!cargandoActividades && actividadesCliente.length === 0 && (
                      <button
                        onClick={async () => {
                          setCargandoActividades(true);
                          try {
                            const response = await receptoresApi.consultarHacienda(receptor.identificacion);
                            if (response.data?.success && response.data?.data?.actividades) {
                              setActividadesCliente(response.data.data.actividades);
                            }
                          } catch (error) {
                            console.error('Error fetching activities:', error);
                          } finally {
                            setCargandoActividades(false);
                          }
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        Cargar
                      </button>
                    )}
                  </div>
                  {cargandoActividades ? (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <div className="animate-spin h-3 w-3 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                      Consultando Hacienda...
                    </div>
                  ) : actividadesCliente.length > 0 ? (
                    <select
                      value={receptor.actividadEconomica || receptor.actividad_economica || ''}
                      onChange={(e) => {
                        const act = actividadesCliente.find(a => a.codigo === e.target.value);
                        if (act) actualizarActividadCliente(act.codigo, act.descripcion);
                      }}
                      className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Seleccionar actividad...</option>
                      {actividadesCliente.map((act, idx) => (
                        <option key={idx} value={act.codigo}>
                          {act.codigo} - {act.descripcion?.substring(0, 40)}...
                        </option>
                      ))}
                    </select>
                  ) : (receptor.actividadEconomica || receptor.actividad_economica) ? (
                    <p className="text-xs font-mono text-gray-700">
                      {receptor.actividadEconomica || receptor.actividad_economica} - {(receptor.actividadEconomicaDescripcion || receptor.actividad_economica_descripcion)?.substring(0, 30)}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400">Sin actividad - click "Cargar"</p>
                  )}
                </div>
              </div>
            ) : (
              <div
                onClick={() => setShowClientModal(true)}
                className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                <User className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">Click para asignar cliente</p>
              </div>
            )}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Selector de tarifa de IVA global para toda la factura */}
          {lineas.length > 0 && tarifasIva.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-200">
              <select
                onChange={(e) => cambiarTarifaTodasLineas(e.target.value)}
                value={lineas[0]?.codigoTarifa || '08'}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-blue-500 focus:border-blue-500"
              >
                {tarifasIva.map(tarifa => (
                  <option key={tarifa.id} value={tarifa.codigo}>
                    IVA: {tarifa.descripcion}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Totals */}
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">{formatCurrency(calcularSubtotal() + calcularDescuento())}</span>
              </div>
              {calcularDescuento() > 0 && (
                <div className="flex justify-between text-sm text-red-600">
                  <span>Descuento:</span>
                  <span className="font-medium">-{formatCurrency(calcularDescuento())}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">IVA:</span>
                {/* Mostramos IVA bruto para que (Subtotal + IVA − Exoneración) cuadre
                    visualmente con el Total. calcularIVA() ya viene NETO de exoneración
                    (ver calcularTotal), por eso sumamos el monto exonerado de vuelta. */}
                <span className="font-medium">{formatCurrency(calcularIVA() + calcularMontoExonerado())}</span>
              </div>
              {/* Impuestos adicionales (911, Cruz Roja) */}
              {calcularImpuestosAdicionales().map(imp => (
                <div key={`${imp.codigoImpuesto}-${imp.codigo}`} className="flex justify-between text-sm text-orange-600">
                  <span>{imp.descripcion} ({imp.porcentaje}%):</span>
                  <span className="font-medium">{formatCurrency(imp.monto)}</span>
                </div>
              ))}
              {tieneExoneracionActiva() && calcularMontoExonerado() > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span className="flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    Exoneración ({getPorcentajeExoneracion()}%):
                  </span>
                  <span className="font-medium">-{formatCurrency(calcularMontoExonerado())}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Items:</span>
                <span className="font-medium">{lineas.length}</span>
              </div>
            </div>

            <div className="bg-slate-800 text-white rounded-xl p-4 mb-4">
              <div className="text-sm text-gray-300 mb-1">Total a Pagar</div>
              <div className="text-3xl font-bold">{formatCurrency(calcularTotal())}</div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  const ejecutar = () => {
                    setReceptor(null);
                    setLineas([]);
                    setDocumento(prev => ({
                      tipoDocumento: prev.tipoDocumento,
                      condicionVenta: '02',
                      medioPago: '01',
                      moneda: 'USD',
                      tipoCambio: storeTipoCambio || 1,
                      comentarios: '',
                      diasCredito: 30,
                      ordenCompra: ''
                    }));
                    setSelectedLineIds([]);
                    setDocumentoId(null);
                    setEsRecurrente(false); setRecurringConfig(null);
                    setIsRejectedEdit(false);
                    setRejectedConsecutivo(null);
                    localStorage.removeItem('factura_en_progreso');
                  };

                  if (lineas.length > 0) {
                    showConfirm(
                      '¿Cancelar y descartar?',
                      'Se perderán todos los datos de la factura actual.',
                      ejecutar,
                      'danger'
                    );
                  } else {
                    ejecutar();
                  }
                }}
                disabled={loading}
                className="flex items-center justify-center gap-1 px-3 py-2 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 text-sm"
              >
                <X className="h-4 w-4" />
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (lineas.length === 0) {
                    toast.error('Agregue al menos un producto/servicio');
                    return;
                  }
                  setShowFacturarModal(true);
                }}
                disabled={loading || lineas.length === 0}
                className="flex-1 flex items-center justify-center gap-1 px-3 py-2 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 hover:border-green-600 transition-colors disabled:opacity-50 font-semibold"
              >
                <Send className="h-5 w-5" />
                Facturar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Client search modal */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Buscar Cliente</h2>
              <button onClick={() => setShowClientModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={clientSearchTerm}
                  onChange={(e) => {
                    setClientSearchTerm(e.target.value);
                    setClientHighlightedIndex(-1);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setClientHighlightedIndex(prev =>
                        prev < clientSearchResults.length - 1 ? prev + 1 : prev
                      );
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setClientHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                    } else if (e.key === 'Enter' && clientHighlightedIndex >= 0) {
                      e.preventDefault();
                      seleccionarCliente(clientSearchResults[clientHighlightedIndex]);
                    } else if (e.key === 'Escape') {
                      setShowClientModal(false);
                    }
                  }}
                  placeholder="Buscar por nombre o cédula..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>

              <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                {searchingClient ? (
                  <div className="py-8 text-center text-gray-500">Buscando...</div>
                ) : clientSearchResults.length > 0 ? (
                  <table className="w-full table-fixed">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-left text-xs font-semibold text-gray-600">
                        <th className="px-4 py-3 w-[40%]">Cliente</th>
                        <th className="px-4 py-3 w-[15%]">Identificación</th>
                        <th className="px-4 py-3 w-[35%]">Email Facturación</th>
                        <th className="px-4 py-3 w-[10%] text-center">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {clientSearchResults.map((cliente, index) => (
                        <tr
                          key={cliente.id}
                          onClick={() => seleccionarCliente(cliente)}
                          onMouseEnter={() => setClientHighlightedIndex(index)}
                          className={`cursor-pointer transition-colors ${
                            index === clientHighlightedIndex
                              ? 'bg-blue-50'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900 truncate" title={cliente.nombre}>{cliente.nombre}</p>
                            {cliente.nombreComercial && cliente.nombreComercial !== cliente.nombre && (
                              <p className="text-xs text-gray-500 truncate">{cliente.nombreComercial}</p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-sm text-gray-700">{cliente.identificacion}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-600 block truncate" title={cliente.emailFacturacion || cliente.email_facturacion || cliente.email || ''}>
                              {cliente.emailFacturacion || cliente.email_facturacion || cliente.email || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {!!(cliente.exoneracionActiva || cliente.exoneracion_activa) && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                  <Shield className="h-3 w-3" />
                                  Exonerado
                                </span>
                              )}
                              {!!(cliente.tieneCuentaCredito || cliente.tiene_cuenta_credito) && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                  <CreditCard className="h-3 w-3" />
                                  Crédito
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : clientSearchTerm.length >= 2 ? (
                  <div className="py-8 text-center text-gray-500">No se encontraron clientes</div>
                ) : (
                  <div className="py-8 text-center text-gray-500">Escriba para buscar clientes</div>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500">Use ↑↓ para navegar, Enter para seleccionar</p>
            </div>

            {/* Quick add new client */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowNuevoClienteModal(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50"
              >
                <UserPlus className="h-4 w-4" />
                Crear Nuevo Cliente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Crear Nuevo Cliente - usando FormularioCliente embebido */}
      {showNuevoClienteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold">Nuevo Cliente</h2>
              <button onClick={() => setShowNuevoClienteModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              <FormularioCliente
                isModal={true}
                onSave={async (clienteCreado) => {
                  setShowNuevoClienteModal(false);
                  setShowClientModal(false);
                  if (clienteCreado) {
                    await seleccionarCliente(clienteCreado);
                  }
                }}
                onCancel={() => setShowNuevoClienteModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Crear Nuevo Producto - usando FormularioProducto embebido */}
      {showNuevoProductoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold">Nuevo Producto</h2>
              <button onClick={() => setShowNuevoProductoModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              <FormularioProducto
                isModal={true}
                initialNombre={nuevoProductoNombre}
                onSuccess={(productoCreado) => {
                  setShowNuevoProductoModal(false);
                  setProductSearchTerm('');
                  if (productoCreado) {
                    // Add the created product to the invoice
                    seleccionarProducto({
                      id: productoCreado.id,
                      nombre: productoCreado.nombre,
                      descripcion: productoCreado.nombre,
                      codigo: productoCreado.codigo,
                      codigoCabys: productoCreado.codigoCabys,
                      precio: productoCreado.precio,
                      tarifaIva: productoCreado.tarifaIva,
                      codigoTarifa: productoCreado.codigoTarifa || productoCreado.codigoTarifaIva,
                      unidadMedida: productoCreado.unidadMedida,
                      moneda: productoCreado.moneda,
                      impuestos: []
                    });
                  }
                }}
                onCancel={() => setShowNuevoProductoModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Comments modal */}
      {showComments && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Comentarios / Notas</h2>
              <button onClick={() => setShowComments(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              <textarea
                value={documento.comentarios}
                onChange={(e) => setDocumento({ ...documento, comentarios: e.target.value })}
                placeholder="Agregar notas o comentarios a la factura..."
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => { setDocumento({ ...documento, comentarios: '' }); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Limpiar
              </button>
              <button
                onClick={() => setShowComments(false)}
                className="px-4 py-2 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Opciones de Factura modal */}
      {showOpcionesFactura && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          {/* Dos columnas: en una sola columna esto era una tira vertical con scroll. */}
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Opciones de Factura</h2>
              <button onClick={() => setShowOpcionesFactura(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              {/* ── Columna izquierda: condiciones de la factura ── */}
              <div className="space-y-4">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Condiciones</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Términos de Pago</label>
                  <select
                    value={documento.diasCredito}
                    onChange={(e) => setDocumento({ ...documento, diasCredito: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={0}>Contado</option>
                    <option value={8}>Neto 8 días</option>
                    <option value={15}>Neto 15 días</option>
                    <option value={30}>Neto 30 días</option>
                    <option value={45}>Neto 45 días</option>
                    <option value={60}>Neto 60 días</option>
                    <option value={90}>Neto 90 días</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
                    <select
                      value={documento.moneda}
                      onChange={(e) => setDocumento({ ...documento, moneda: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="CRC">₡ Colones (CRC)</option>
                      <option value="USD">$ Dólares (USD)</option>
                    </select>
                  </div>
                  {documento.moneda === 'USD' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Cambio</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₡</span>
                        <input
                          type="number"
                          step="0.01"
                          value={documento.tipoCambio}
                          onChange={(e) => setDocumento({ ...documento, tipoCambio: parseFloat(e.target.value) || 1 })}
                          className="w-full pl-7 pr-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">BCCR: ₡{storeTipoCambio?.toFixed(2) || '---'}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referencia / Orden de Compra</label>
                  <input
                    type="text"
                    value={documento.ordenCompra}
                    onChange={(e) => setDocumento({ ...documento, ordenCompra: e.target.value })}
                    placeholder="Ej: PO-2024-001234"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Se incluirá en las notas de la factura</p>
                </div>
              </div>

              {/* ── Columna derecha: periodo de cobro ── */}
              <div className="space-y-3 sm:border-l sm:border-gray-200 sm:pl-6">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Periodo de cobro</h3>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={periodoCobro.activo}
                    onChange={(e) => setPeriodoCobro(prev => ({ ...prev, activo: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Incluirlo en la factura</span>
                </label>

                {!periodoCobro.activo ? (
                  <p className="text-xs text-gray-400">
                    Al activarlo se agrega una línea al pie del detalle del PDF, por ejemplo
                    “Periodo de Cobro: Septiembre 2026”.
                  </p>
                ) : periodoCobro.libre ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Texto del periodo</label>
                    <input
                      type="text"
                      value={periodoCobro.libre}
                      onChange={(e) => setPeriodoCobro(prev => ({ ...prev, libre: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setPeriodoCobro(prev => ({ ...prev, libre: undefined }))}
                      className="text-xs text-blue-600 hover:underline mt-1"
                    >
                      Usar el formato estándar
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-1.5">
                      {[
                        { l: 'Mes anterior', m: -1 },
                        { l: 'Actual', m: 0 },
                        { l: 'Siguiente', m: 1 },
                      ].map(({ l, m }) => (
                        <button
                          key={l}
                          type="button"
                          onClick={() => {
                            const d = new Date();
                            d.setMonth(d.getMonth() + m);
                            setPeriodoCobro(prev => ({ ...prev, tipo: 'mes', mes: d.getMonth(), anio: d.getFullYear() }));
                          }}
                          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                          {l}
                        </button>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Tipo</label>
                        <select
                          value={periodoCobro.tipo}
                          onChange={(e) => setPeriodoCobro(prev => ({ ...prev, tipo: e.target.value }))}
                          className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="mes">Mensual</option>
                          <option value="anio">Anual</option>
                        </select>
                      </div>
                      {periodoCobro.tipo === 'mes' ? (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Mes</label>
                          <select
                            value={periodoCobro.mes}
                            onChange={(e) => setPeriodoCobro(prev => ({ ...prev, mes: parseInt(e.target.value) }))}
                            className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          >
                            {MESES_PERIODO.map((m, i) => <option key={m} value={i}>{m}</option>)}
                          </select>
                        </div>
                      ) : <div />}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Año</label>
                        <input
                          type="number"
                          value={periodoCobro.anio}
                          onChange={(e) => setPeriodoCobro(prev => ({ ...prev, anio: parseInt(e.target.value) || new Date().getFullYear() }))}
                          className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Etiqueta</label>
                        <input
                          type="text"
                          value={periodoCobro.etiqueta}
                          onChange={(e) => setPeriodoCobro(prev => ({ ...prev, etiqueta: e.target.value }))}
                          placeholder="Periodo de Cobro"
                          className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </>
                )}

                {periodoCobro.activo && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-center">
                    <span className="text-sm font-semibold text-blue-700">
                      {armarPeriodoCobro(periodoCobro) || '—'}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowOpcionesFactura(false)}
                className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <DraggableModal title="Vista Previa - Factura Electrónica" onClose={() => setShowPreview(false)} maxWidth="max-w-4xl">
            <div className="p-4 overflow-auto h-full bg-gray-100">
              <InvoiceTemplate
                emisor={emisorData || currentEmisor}
                receptor={receptor ? {
                  nombre: receptor.nombre,
                  identificacion: receptor.identificacion,
                  tipo_identificacion: receptor.tipoIdentificacion || receptor.tipo_identificacion,
                  email: receptor.email,
                  email_facturacion: receptor.emailFacturacion || receptor.email_facturacion || receptor.email,
                  telefono_codigo: receptor.telefonoCodigo || receptor.telefono_codigo || '506',
                  telefono_numero: receptor.telefonoNumero || receptor.telefono_numero,
                  otras_senas: receptor.otrasSenas || receptor.otras_senas,
                  provincia: receptor.provincia,
                  canton: receptor.canton,
                  distrito: receptor.distrito,
                  dias_credito: receptor.diasCredito || receptor.dias_credito || 30
                } : null}
                documento={{
                  tipoDocumento: documento.tipoDocumento,
                  consecutivo: '00100001010000000000',
                  fechaEmision: new Date().toISOString(),
                  codigoMoneda: documento.moneda,
                  referenciaInterna: documento.referenciaInterna,
                  ordenCompra: documento.ordenCompra,
                  notas: documento.comentarios,
                  periodoCobroTexto: armarPeriodoCobro(periodoCobro),
                  diasCredito: receptor?.diasCredito || receptor?.dias_credito || 30
                }}
                lineas={lineas.map(l => {
                  const subtotal = l.cantidad * l.precioUnitario;
                  const descuentoMonto = subtotal * ((l.descuento || 0) / 100);
                  const baseIva = subtotal - descuentoMonto;
                  const ivaMonto = baseIva * (l.iva / 100);
                  const tieneExo = lineaTieneExoneracion(l);
                  const porcentajeExo = getPorcentajeExoneracion();
                  const montoExonerado = tieneExo ? baseIva * (Math.min(porcentajeExo, l.iva) / 100) : 0;

                  // Calculate net tax after exoneration
                  const impuestoNeto = ivaMonto - montoExonerado;

                  return {
                    codigo: l.codigoProducto || l.codigo,
                    sku: l.sku,
                    detalle: l.detalle,
                    cantidad: l.cantidad,
                    precioUnitario: l.precioUnitario,
                    descuentoMonto: descuentoMonto,
                    impuestoTarifa: l.iva,
                    impuestoCodigoTarifa: l.codigoTarifa || '08',
                    impuestoMonto: ivaMonto,
                    impuestoNeto: impuestoNeto,
                    montoTotalLinea: baseIva + impuestoNeto,
                    // Pasar los impuestos adicionales (Cruz Roja + 911 + otros) — sin esto
                    // el InvoiceTemplate los suma como 0 y muestra "Otros: $0" + total incorrecto
                    impuestosAdicionales: (l.impuestosAdicionales || []).map(imp => ({
                      codigoImpuesto: imp.codigoImpuesto,
                      codigo: imp.codigo,
                      descripcion: imp.descripcion,
                      porcentaje: parseFloat(imp.porcentaje) || 0,
                      monto: Math.round(baseIva * (parseFloat(imp.porcentaje) || 0) / 100 * 100) / 100
                    })),
                    impuestoExoneracion: tieneExo ? (() => {
                      const exoData = getExoneracionData();
                      return {
                        tipoDocumento: exoData.tipoDocumento,
                        tipoDocumentoDesc: exoData.tipoDocumentoDesc,
                        numeroDocumento: exoData.numeroDocumento,
                        nombreInstitucion: exoData.nombreInstitucion,
                        articulo: exoData.articulo,
                        inciso: exoData.inciso,
                        fechaEmision: exoData.fechaEmision,
                        porcentaje: porcentajeExo,
                        monto: montoExonerado
                      };
                    })() : null
                  };
                })}
                totales={(() => {
                  // Exonerado = Exento (sin IVA)
                  let gravado = 0;
                  let exento = 0;
                  lineas.forEach(l => {
                    const subtotal = (l.cantidad || 0) * (l.precioUnitario || 0);
                    if (lineaTieneExoneracion(l) || (l.iva || 0) === 0) {
                      exento += subtotal;
                    } else {
                      gravado += subtotal;
                    }
                  });
                  return {
                    totalGravado: gravado,
                    totalExento: exento,
                    totalImpuesto: calcularIVA() + calcularTotalImpuestosAdicionales(),
                    totalDescuentos: calcularDescuento(),
                    totalVenta: calcularSubtotal() + calcularIVA() + calcularTotalImpuestosAdicionales(),
                    totalComprobante: calcularSubtotal() + calcularIVA() + calcularTotalImpuestosAdicionales(),
                    impuestosDesglose: [
                      { descripcion: 'IVA', monto: calcularIVA() },
                      ...calcularImpuestosAdicionales()
                    ].filter(i => i.monto > 0)
                  };
                })()}
                esCotizacion={false}
                tipoCambio={documento.moneda === 'USD' ? documento.tipoCambio || 1 : 1}
              />
            </div>
        </DraggableModal>
      )}

      {/* Modal Validación Exoneración */}
      {showExoneracionModal && exoneracionValidation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-amber-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Validación de Exoneración</h2>
                  <p className="text-sm text-gray-600">
                    Exoneración: {exoneracionValidation.exoneracion?.numero} ({exoneracionValidation.exoneracion?.porcentaje}%)
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowExoneracionModal(false)}
                className="p-1 hover:bg-amber-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <p className="text-gray-700 mb-4">
                El cliente tiene exoneración activa, pero algunos productos no coinciden con los códigos CABYS autorizados en la exoneración.
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Productos sin coincidencia */}
                <div className="border border-red-200 rounded-lg overflow-hidden">
                  <div className="bg-red-50 px-4 py-2 border-b border-red-200">
                    <h3 className="font-semibold text-red-800 flex items-center gap-2">
                      <XCircle className="h-4 w-4" />
                      Productos sin Coincidencia ({exoneracionValidation.lineasSinMatch?.length || 0})
                    </h3>
                  </div>
                  <div className="p-3 max-h-60 overflow-y-auto space-y-2">
                    {exoneracionValidation.lineasSinMatch?.map((linea, idx) => (
                      <div key={idx} className="bg-white border border-gray-200 rounded p-2">
                        <p className="font-medium text-sm text-gray-900 truncate">{linea.descripcion}</p>
                        <p className="text-xs text-gray-500 font-mono">CABYS: {linea.cabys || 'Sin CABYS'}</p>
                        <p className="text-xs text-gray-500">Cantidad: {linea.cantidad} | Total: {linea.total?.toLocaleString('es-CR', { style: 'currency', currency: documento.moneda })}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CABYS autorizados */}
                <div className="border border-purple-200 rounded-lg overflow-hidden">
                  <div className="bg-purple-50 px-4 py-2 border-b border-purple-200">
                    <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      CABYS Autorizados en Exoneración
                    </h3>
                  </div>
                  <div className="p-3 max-h-60 overflow-y-auto">
                    <div className="flex flex-wrap gap-2">
                      {exoneracionValidation.cabysAutorizados?.map((cabys, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-mono"
                        >
                          {cabys}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Productos con coincidencia */}
              {exoneracionValidation.lineasConMatch?.length > 0 && (
                <div className="mt-4 border border-green-200 rounded-lg overflow-hidden">
                  <div className="bg-green-50 px-4 py-2 border-b border-green-200">
                    <h3 className="font-semibold text-green-800 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Productos con Exoneración ({exoneracionValidation.lineasConMatch?.length})
                    </h3>
                  </div>
                  <div className="p-3 max-h-40 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-2">
                      {exoneracionValidation.lineasConMatch?.map((linea, idx) => (
                        <div key={idx} className="text-xs bg-green-50 border border-green-200 rounded p-2">
                          <p className="font-medium text-gray-900 truncate">{linea.descripcion}</p>
                          <p className="text-green-600 font-mono">CABYS: {linea.cabys}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Info */}
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Opciones:</strong>
                </p>
                <ul className="text-sm text-blue-700 mt-1 space-y-1">
                  <li>• <strong>Continuar sin exoneración:</strong> Los productos sin coincidencia pagarán IVA normalmente ({exoneracionValidation.lineasConMatch?.length || 0} productos sí tendrán exoneración)</li>
                  <li>• <strong>Cancelar:</strong> Regrese al formulario y ajuste los códigos CABYS de los productos</li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowExoneracionModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowExoneracionModal(false);
                  // Continuar sin validación de exoneración para productos sin match
                  handleGuardar(pendingEnviar, true);
                }}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2"
              >
                <AlertTriangle className="h-4 w-4" />
                Continuar sin Exoneración
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay de procesamiento */}
      {loading && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="text-center">
            <div className="relative mx-auto mb-4 w-16 h-16">
              <div className="absolute inset-0 rounded-full border-4 border-blue-200"></div>
              <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin"></div>
            </div>
            <p className="text-lg font-semibold text-gray-800">Procesando factura...</p>
            <p className="text-sm text-gray-500 mt-1">Enviando a Hacienda</p>
          </div>
        </div>
      )}

      {/* Modal Facturar — selección Contado/Crédito con info del cliente */}
      {showFacturarModal && (
        <CreditConfirmModal
          receptorId={receptor?.id}
          montoTotal={calcularTotal()}
          moneda={documento.moneda}
          tipoCambio={documento.tipoCambio}
          mode="facturar"
          onContado={() => {
            setShowFacturarModal(false);
            setDocumento({...documento, condicionVenta: '01'});
            handleGuardar(true, false, true, '01');
          }}
          onCredito={() => {
            setShowFacturarModal(false);
            setDocumento({...documento, condicionVenta: '02'});
            handleGuardar(true, false, true, '02');
          }}
          onCancel={() => setShowFacturarModal(false)}
        />
      )}

      {/* Modal Exoneración */}
      <ExoneracionModal
        isOpen={showExoneracionRapidaModal}
        onClose={() => setShowExoneracionRapidaModal(false)}
        onExoneracionSelected={setExoneracionTemporal}
        clienteIdentificacion={receptor?.identificacion}
        clienteNombre={receptor?.nombre}
        subtitulo="Solo para esta factura"
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText="Sí, continuar"
        cancelText="No, volver"
      />

      {/* Modal de configuración de plantilla recurrente */}
      {showRecurrenteModal && (
        <RecurrenteConfigModal
          initialConfig={recurringConfig}
          defaultNombre={receptor?.nombre || ''}
          onClose={() => setShowRecurrenteModal(false)}
          onConfirm={(config) => {
            setRecurringConfig(config);
            setEsRecurrente(true);
            setShowRecurrenteModal(false);
          }}
        />
      )}
    </div>
  );
}
